import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Ticket, Phone, MapPin, Calendar, Star, Users, Wine, Camera, Music, Heart, Sun, Moon, Award, Anchor, Crown, Briefcase, PartyPopper, Cake } from "lucide-react";
import { CONTACT, MAGIC_PHOTOS, GALLERY, TESTIMONIALS } from "../data/cruiseData";
import EnquiryForm from "../components/EnquiryForm";
import AvailabilityCalendar from "../components/AvailabilityCalendar";
import BookingForm from "../components/BookingForm";
import BookingTicker from "../components/BookingTicker";
import FAQSection from "../components/FAQSection";
import { Lightbox } from "../components/Lightbox";
import SEOHead from "../components/SEOHead";
import HomeTicketedCards from "../components/HomeTicketedCards";
import useFeaturedTicket from "../hooks/useFeaturedTicket";

const MOMENTS = [
 { eyebrow: "Hero · 28 May, 19 Jun", title: "Vivid Sydney", line: "Inside Sydney's biggest winter festival, on the water.", img: MAGIC_PHOTOS.vividHarbour, link: "/cruises/vivid-cruises-sydney", accent: "fuchsia" },
 { eyebrow: "All season · 10 to 120 guests", title: "Birthday Cruises", line: "Top deck dance floor. Full bar. Skyline backdrop.", img: MAGIC_PHOTOS.birthdayHarbourMagic, link: "/cruises/birthday-cruises-sydney", accent: "pink" },
 { eyebrow: "31 December · Selling fast", title: "NYE 2026", line: "Front row to Sydney's fireworks.", img: MAGIC_PHOTOS.nyeFireworksHero, link: "/cruises/nye-cruises-sydney-harbour", accent: "amber" }];

const EXPERIENCES = [
 { title: "Weddings", desc: "Ceremony on the bow at sunset. Reception under the stars.", img: MAGIC_PHOTOS.weddingBrideGroomSunset, link: "/cruises/wedding-cruises-sydney", Icon: Heart },
 { title: "Birthdays", desc: "Top-deck dance floor. Full bar. Sydney's skyline as your backdrop.", img: MAGIC_PHOTOS.birthdayFull, link: "/cruises/birthday-cruises-sydney", Icon: Star },
 { title: "Hens & Bachelorettes", desc: "Pink balloons, champagne, and 50 of your favourite people.", img: MAGIC_PHOTOS.hensBridal, link: "/cruises/hens-party-cruises-sydney-harbour", Icon: Crown },
 { title: "Corporate", desc: "Christmas parties, EOFY, product launches. Out of the office, on the water.", img: MAGIC_PHOTOS.corporateChampagne, link: "/cruises/corporate-cruises-sydney-harbour", Icon: Users },
 { title: "BYO Cruise", desc: "Bring your drinks. Zero corkage. The only BYO charter in Sydney.", img: MAGIC_PHOTOS.byoSunsetDeck, link: "/cruises/byo-cruise-sydney-harbour", Icon: Wine },
 { title: "Sunset Cruise", desc: "Golden hour over the harbour. Drink in hand. Music low.", img: MAGIC_PHOTOS.familyBowSunset, link: "/cruises/sunset-cruises-sydney-harbour", Icon: Sun }];

// 8-slide rotation, cinematic cool purple/violet/blue palette with subtle per-slide accent tones
const HERO_SLIDES = [
 {
  img: MAGIC_PHOTOS.harbourSunsetGroup,
  tag: "Private Charters",
  alt: "Private charter on Sydney Harbour · Opera House + Bridge",
  quote: "Cheers, Sydney.",
  hashtag: "#SydneyHarbour #PrivateCharter",
  // Cool violet-blue with warm rose hint
  tint: "linear-gradient(135deg, hsla(255,75%,30%,0.65) 0%, hsla(225,80%,22%,0.6) 45%, hsla(285,60%,28%,0.5) 80%, hsla(220,80%,8%,0.85) 100%)",
  accent: "hsl(48, 100%, 78%)",
  theme: "sunset",
 },
 {
  img: MAGIC_PHOTOS.weddingBrideGroomSunset,
  tag: "Wedding Cruises",
  alt: "Sydney Harbour wedding cruises · bride with white rose bouquet and groom in tuxedo raising champagne on the bow of MV Sydney Magic with the Harbour Bridge and Opera House behind",
  quote: "I do. On the harbour.",
  hashtag: "#WeddingCruises #SydneyHarbour",
  // Lavender-rose-violet
  tint: "linear-gradient(135deg, hsla(280,70%,30%,0.6) 0%, hsla(330,55%,28%,0.55) 45%, hsla(255,65%,25%,0.6) 80%, hsla(220,75%,10%,0.85) 100%)",
  accent: "hsl(45, 100%, 80%)",
  theme: "wedding",
 },
 {
  img: MAGIC_PHOTOS.hensPinkParty,
  tag: "Birthday Cruises",
  alt: "Birthday cruise on MV Sydney Magic · candid celebration",
  quote: "Birthday on the bow.",
  hashtag: "#BirthdayCruise #SydneyHarbour",
  // Vivid magenta-violet
  tint: "linear-gradient(135deg, hsla(310,70%,30%,0.6) 0%, hsla(275,75%,25%,0.55) 45%, hsla(230,70%,22%,0.55) 80%, hsla(220,80%,8%,0.85) 100%)",
  accent: "hsl(48, 100%, 80%)",
  theme: "birthday",
 },
 {
  img: MAGIC_PHOTOS.corporateGroupSkyline,
  tag: "Corporate Cruises",
  alt: "Corporate boat hire on Sydney Harbour · business-casual guests on the front deck of MV Sydney Magic with Harbour Bridge + Opera House + CBD skyline",
  quote: "Boardroom on the bow.",
  hashtag: "#CorporateCruise #Sydney",
  // Deep electric blue-violet
  tint: "linear-gradient(135deg, hsla(225,85%,28%,0.65) 0%, hsla(265,70%,25%,0.6) 50%, hsla(215,80%,18%,0.6) 100%)",
  accent: "hsl(48, 100%, 80%)",
  theme: "corporate",
 },
 {
  img: MAGIC_PHOTOS.birthdaySunsetGroupBow,
  tag: "Family Celebrations",
  alt: "Family celebration on the bow of MV Sydney Magic at golden hour",
  quote: "Three generations. One sunset.",
  hashtag: "#FamilyCruise #SydneyHarbour",
  // Warm violet-rose
  tint: "linear-gradient(135deg, hsla(270,60%,28%,0.55) 0%, hsla(320,55%,28%,0.5) 50%, hsla(245,65%,22%,0.55) 80%, hsla(220,75%,10%,0.85) 100%)",
  accent: "hsl(48, 100%, 80%)",
  theme: "sunset",
 },
 {
  img: MAGIC_PHOTOS.nyeFireworksHero,
  tag: "NYE Fireworks",
  alt: "Sydney NYE fireworks from the harbour",
  quote: "Fireworks. Front row.",
  hashtag: "#NYESydney #FireworksCruise",
  // Deep midnight purple
  tint: "linear-gradient(135deg, hsla(265,85%,25%,0.65) 0%, hsla(290,80%,22%,0.6) 45%, hsla(220,75%,12%,0.85) 100%)",
  accent: "hsl(195, 100%, 78%)",
  theme: "nye",
 },
 {
  img: MAGIC_PHOTOS.hensBridal,
  tag: "Hens & Bridal",
  alt: "Bridal party sashes on the harbour · Opera House",
  quote: "Bride squad. Boat life.",
  hashtag: "#HensCruise #SydneyHarbour",
  // Lavender-magenta
  tint: "linear-gradient(135deg, hsla(295,68%,30%,0.6) 0%, hsla(335,60%,28%,0.55) 45%, hsla(255,65%,22%,0.55) 80%, hsla(220,80%,10%,0.85) 100%)",
  accent: "hsl(48, 100%, 80%)",
  theme: "hens",
 },
 {
  img: MAGIC_PHOTOS.womensGroupChampagne,
  tag: "Women's Group Cruises",
  alt: "Sydney Harbour cruises for women's groups · private boat cruises Sydney on MV Sydney Magic · champagne on the sun deck",
  quote: "Chin chin, ladies.",
  hashtag: "#WomensGroupCruise #SydneyHarbour",
  // Warm rose + amber + violet
  tint: "linear-gradient(135deg, hsla(330,60%,32%,0.55) 0%, hsla(35,70%,32%,0.45) 45%, hsla(275,55%,26%,0.55) 80%, hsla(220,75%,10%,0.85) 100%)",
  accent: "hsl(45, 100%, 80%)",
  theme: "hens",
 },
 {
  img: MAGIC_PHOTOS.vividMagicBoat,
  tag: "Vivid Sydney",
  alt: "MV Sydney Magic cruising at Vivid Sydney · neon Opera House",
  quote: "Light show. Inside the festival.",
  hashtag: "#VividSydney #VividCruise",
  // Electric blue-violet (neon)
  tint: "linear-gradient(135deg, hsla(225,90%,28%,0.65) 0%, hsla(285,80%,28%,0.6) 50%, hsla(220,85%,12%,0.85) 100%)",
  accent: "hsl(195, 100%, 78%)",
  theme: "vivid",
 }];

const Section = ({ n, label, Icon }) => (
 <div className="flex items-center gap-4 mb-5">
  <span className="w-10 h-10 rounded-full ring-2 ring-amber-300/60 bg-amber-300/10 flex items-center justify-center text-amber-300 text-sm font-black tabular-nums">{String(n).padStart(2, "0")}</span>
  <span className="text-amber-300 text-[11px] uppercase tracking-[0.32em] font-bold flex items-center gap-2">{Icon && <Icon className="w-3.5 h-3.5" />} {label}</span>
 </div>
);

/* ── Themed slide decorations, floating SVG elements per slide theme (sparklers, balloons, confetti, snow…) ── */
const ThemeDecor = ({ theme }) => {
 if (theme === "nye") {
  // Sparkler bursts + golden particles
  return (
   <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true, ">
    {[{ top: "8%", left: "6%", s: 220, d: "0s" }, { top: "16%", left: "82%", s: 280, d: "-3s" }, { top: "62%", left: "14%", s: 160, d: "-1.5s" }, { top: "38%", left: "70%", s: 200, d: "-4s" }].map((b, i) => (
     <svg key={i} className="absolute opacity-70 decor-twinkle" style={{ top: b.top, left: b.left, width: b.s, height: b.s, animationDelay: b.d }} viewBox="0 0 100 100">
      <defs><radialGradient id={`sp-${i}`}><stop offset="0%" stopColor="hsla(45,100%,85%,1)" /><stop offset="50%" stopColor="hsla(40,100%,70%,0.6)" /><stop offset="100%" stopColor="transparent" /></radialGradient></defs>
      {Array.from({ length: 14 }).map((_, k) => { const a = (k * 360) / 14; return <line key={k} x1="50" y1="50" x2={50 + 46 * Math.cos((a * Math.PI) / 180)} y2={50 + 46 * Math.sin((a * Math.PI) / 180)} stroke={`url(#sp-${i})`} strokeWidth="1.2" strokeLinecap="round" />; })}
      <circle cx="50" cy="50" r="6" fill={`url(#sp-${i})`} />
     </svg>
    ))}
    {Array.from({ length: 16 }).map((_, i) => <span key={`c-${i}`} className="decor-confetti" style={{ left: `${Math.random() * 100}%`, animationDelay: `${-Math.random() * 8}s`, "--c": ["hsl(45,100%,65%)", "hsl(335,95%,70%)", "hsl(195,100%,72%)", "hsl(280,85%,70%)"][i % 4] }} />)}
   </div>
  );
 }
 if (theme === "birthday" || theme === "hens") {
  const palette = theme === "hens" ? ["hsl(340,100%,75%)", "hsl(280,75%,70%)", "hsl(45,100%,75%)", "hsl(195,100%,75%)"] : ["hsl(335,95%,70%)", "hsl(45,100%,68%)", "hsl(280,85%,70%)", "hsl(195,100%,72%)"];
  return (
   <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true, ">
    {Array.from({ length: 10 }).map((_, i) => (
     <span key={`b-${i}`} className="decor-balloon" style={{
      left: `${5 + Math.random() * 90}%`,
      top: `${Math.random() * 50}%`,"--col": palette[i % palette.length],"--size": `${30 + Math.random() * 28}px`,
      animationDelay: `${-Math.random() * 14}s`,
      animationDuration: `${14 + Math.random() * 10}s`,
     }} />
    ))}
    {Array.from({ length: 18 }).map((_, i) => <span key={`c-${i}`} className="decor-confetti" style={{ left: `${Math.random() * 100}%`, animationDelay: `${-Math.random() * 8}s`,"--c": palette[i % palette.length] }} />)}
   </div>
  );
 }
 if (theme === "wedding") {
  return (
   <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true, ">
    {Array.from({ length: 14 }).map((_, i) => <span key={`p-${i}`} className="decor-petal" style={{ left: `${Math.random() * 100}%`, animationDelay: `${-Math.random() * 16}s`, animationDuration: `${16 + Math.random() * 10}s`,"--rot": `${Math.random() * 360}deg` }} />)}
   </div>
  );
 }
 if (theme === "corporate") {
  return (
   <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true, ">
    {[{ top: "12%", left: "8%", s: 80 }, { top: "20%", left: "85%", s: 110 }, { top: "60%", left: "10%", s: 70 }, { top: "70%", left: "78%", s: 90 }].map((g, i) => (
     <svg key={i} className="absolute opacity-25 decor-twinkle" style={{ top: g.top, left: g.left, width: g.s, height: g.s * 1.6, animationDelay: `${-i * 2}s`, animationDuration: "8s" }} viewBox="0 0 40 64" fill="none" stroke="hsl(195,100%,82%)" strokeWidth="1.2">
      <path d="M10 8 L30 8 L26 26 Q20 32 14 26 Z" />
      <line x1="20" y1="32" x2="20" y2="54" />
      <line x1="12" y1="56" x2="28" y2="56" />
     </svg>
    ))}
   </div>
  );
 }
 if (theme === "christmas") {
  return (
   <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true, ">
    {Array.from({ length: 22 }).map((_, i) => <span key={`s-${i}`} className="decor-snow" style={{ left: `${Math.random() * 100}%`,"--size": `${4 + Math.random() * 6}px`, animationDelay: `${-Math.random() * 18}s`, animationDuration: `${14 + Math.random() * 12}s` }} />)}
   </div>
  );
 }
 if (theme === "vivid") {
  return (
   <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true, ">
    {[{ left: "10%", a: 14, c: "hsl(195,100%,68%)" }, { left: "30%", a: -8, c: "hsl(290,90%,68%)" }, { left: "65%", a: 10, c: "hsl(335,95%,70%)" }, { left: "85%", a: -12, c: "hsl(220,100%,72%)" }].map((b, i) => (
     <span key={i} className="absolute top-[-10%] h-[140%] w-[3px] decor-beam" style={{ left: b.left, background: `linear-gradient(180deg, transparent 0%, ${b.c} 50%, transparent 100%)`, transform: `rotate(${b.a}deg)`, animationDelay: `${-i * 1.5}s` }} />
    ))}
   </div>
  );
 }
 if (theme === "halloween") {
  return (
   <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true, ">
    {/* Flying bats, swooping silhouettes */}
    {[
     { top: "12%", left: "8%", s: 28, d: "0s", dur: "18s" },
     { top: "22%", left: "78%", s: 34, d: "-6s", dur: "22s" },
     { top: "58%", left: "18%", s: 22, d: "-12s", dur: "26s" },
     { top: "38%", left: "62%", s: 30, d: "-3s", dur: "20s" },
     { top: "68%", left: "85%", s: 24, d: "-9s", dur: "24s" }].map((b, i) => (
     <svg key={`bat-${i}`} className="absolute decor-bat" style={{ top: b.top, left: b.left, width: b.s, height: b.s, animationDelay: b.d, animationDuration: b.dur }} viewBox="0 0 24 24" fill="black" aria-hidden="true, ">
      <path d="M12 2 Q10 6 8 7 Q5 4 2 5 Q4 8 5 11 Q3 12 2 14 Q5 13 7 14 Q9 12 12 13 Q15 12 17 14 Q19 13 22 14 Q21 12 19 11 Q20 8 22 5 Q19 4 16 7 Q14 6 12 2 Z" />
     </svg>
    ))}
    {/* Orange spooky embers */}
    {Array.from({ length: 14 }).map((_, i) => (
     <span key={`em-${i}`} className="absolute decor-spook-ember" style={{
      left: `${Math.random() * 100}%`,
      bottom: `${Math.random() * 50}%`,
      width: `${2 + Math.random() * 3}px`,
      height: `${2 + Math.random() * 3}px`,
      animationDelay: `${-Math.random() * 20}s`,
      animationDuration: `${18 + Math.random() * 14}s`,
     }} />
    ))}
    {/* Pumpkin glow at top-left + bottom-right */}
    <div className="absolute top-[15%] left-[12%] w-32 h-32 rounded-full" style={{ background: "radial-gradient(circle, hsla(25,100%,55%,0.45) 0%, transparent 70%)", filter: "blur(20px)" }} />
    <div className="absolute bottom-[15%] right-[10%] w-40 h-40 rounded-full" style={{ background: "radial-gradient(circle, hsla(285,80%,45%,0.4) 0%, transparent 70%)", filter: "blur(24px)" }} />
   </div>
  );
 }
 // sunset (default for Private Charters)
 return (
  <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true, ">
   {Array.from({ length: 6 }).map((_, i) => (
    <span key={`g-${i}`} className="decor-glow-orb" style={{ left: `${Math.random() * 100}%`, top: `${10 + Math.random() * 40}%`,"--size": `${80 + Math.random() * 120}px`, animationDelay: `${-Math.random() * 18}s` }} />
   ))}
  </div>
 );
};

export default function HomeV2() {
 useEffect(() => { document.title = "Magic Cruises · Unforgettable Sydney Harbour Experiences"; }, []);
 const [availISO, setAvailISO] = useState(null);
 const [slideIdx, setSlideIdx] = useState(0);
 const featuredTicket = useFeaturedTicket();
 useEffect(() => {
  const t = setInterval(() => setSlideIdx(i => (i + 1) % HERO_SLIDES.length), 5800);
  return () => clearInterval(t);
 }, []);
 useEffect(() => {
  const io = new IntersectionObserver((entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add("in"); }), { threshold: 0.12 });
  document.querySelectorAll(".reveal-up").forEach(el => io.observe(el));
  return () => io.disconnect();
 }, []);

 return (
  <main className="bg-[hsl(220,55%,5%)] text-white" data-testid="home-v2">
   <SEOHead title="Magic Cruises · Unforgettable Sydney Harbour Experiences" description="Sydney Harbour cruises with vibe. Weddings, NYE fireworks, Vivid, birthdays, corporate parties aboard the MV Sydney Magic. 20 years of unforgettable nights on the water." image={MAGIC_PHOTOS.harbourSunsetGroup} type="TouristAttraction" />

   {/* ════════ HERO, CINEMATIC ENVIRONMENT · crisp photo + light atmosphere · 8-slide rotation ════════ */}
   <section className="relative min-h-[100svh] flex items-end overflow-hidden pt-24 lg:pt-28 pb-16 lg:pb-24 grain-noise" data-testid="home-hero">
    {/* Booking ticker, anchored just below the fixed top nav (TrustBar + nav ≈ 110px), above the hero content */}
    <div className="absolute left-0 right-0 z-30 top-[100px] sm:top-[108px] lg:top-[116px]">
     <BookingTicker />
    </div>
    {/* L1: Rotating BG slides, crossfade · slow Ken Burns on the active one (CRISP, no blur) */}
    <div className="absolute inset-0">
     {HERO_SLIDES.map((s, i) => (
      <img
       key={i}
       src={s.img}
       alt={i === slideIdx ? s.alt : ""}
       className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[1600ms] ease-out ${i === slideIdx ? "opacity-100 cinema-pan" : "opacity-0"}`}
       loading={i === 0 ? "eager" : "lazy"}
       fetchpriority={i === 0 ? "high" : "low"}
       aria-hidden={i === slideIdx ? "false, " : "true, "}
      />
     ))}
    </div>

    {/* L2: PER-SLIDE COLOR TINT, themed gradient cast (crossfades when slide changes) */}
    <div className="absolute inset-0 pointer-events-none" aria-hidden="true, ">
     {HERO_SLIDES.map((s, i) => (
      <div key={i} className={`absolute inset-0 transition-opacity duration-[1600ms] ease-out ${i === slideIdx ? "opacity-100" : "opacity-0"}`} style={{ background: s.tint }} />
     ))}
    </div>

    {/* L2b: per-slide themed decorations (sparklers/balloons/confetti/petals/snow/beams) */}
    {HERO_SLIDES.map((s, i) => (
     <div key={`decor-${i}`} className={`absolute inset-0 pointer-events-none transition-opacity duration-[1200ms] ease-out ${i === slideIdx ? "opacity-100" : "opacity-0"}`} aria-hidden="true, ">
      {i === slideIdx && <ThemeDecor theme={s.theme} />}
     </div>
    ))}

    {/* L3: subtle volumetric god rays, top-right warm sunlight */}
    <div className="cinema-godrays" aria-hidden="true, " />

    {/* L3b: drifting harbour haze, bottom band only, behind text gradient (image stays crisp) */}
    <div className="absolute left-0 right-0 bottom-0 h-[55%] pointer-events-none cinema-haze-soft" aria-hidden="true, " />

    {/* L3c: pulsing skyline glow on the horizon */}
    <div className="cinema-skyline-glow" aria-hidden="true, " />

    {/* L3d: cinematic light bloom, soft expanding halo near the focal point */}
    <div className="cinema-bloom" aria-hidden="true, " />

    {/* L4: very soft cinematic vignette, anchors focal point without washing out image */}
    <div className="absolute inset-0 pointer-events-none" aria-hidden="true, " style={{ background: "radial-gradient(ellipse at 30% 50%, transparent 30%, hsla(220,60%,4%,0.55) 95%)" }} />

    {/* L5: slow drifting lens flares, diagonal arcs */}
    <div className="absolute -top-20 -left-20 w-full h-full" aria-hidden="true, ">
     <div className="cinema-flare" style={{ top: "20%", left: "0%" }} />
     <div className="cinema-flare" style={{ top: "10%", left: "30%", animationDelay: "-25s", animationDuration: "60s" }} />
    </div>

    {/* L6: water reflection, vertical shimmer ribbon on the surface */}
    <div className="cinema-reflection" aria-hidden="true, " />

    {/* L7: waterline glint, slow horizontal sweep */}
    <div className="cinema-glint" aria-hidden="true, " />

    {/* L8: drifting embers + golden motes */}
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true, ">
     {Array.from({ length: 12 }).map((_, i) => {
      const size = 2 + Math.random() * 3;
      const dur = 22 + Math.random() * 16;
      const delay = -Math.random() * dur;
      const x = (Math.random() - 0.5) * 22;
      const op = 0.4 + Math.random() * 0.4;
      return (
       <span key={`e-${i}`} className="cinema-ember" style={{
        left: `${Math.random() * 100}%`,
        bottom: `${Math.random() * 12}%`,"--ember-size": `${size}px`,"--ember-dur": `${dur}s`,"--ember-delay": `${delay}s`,"--ember-x": `${x}vw`,"--ember-opacity": op,
       }} />
      );
     })}
    </div>

    {/* L9: bottom readability gradient, keeps text legible */}
    <div className="absolute inset-0 bg-gradient-to-t from-[hsl(220,55%,5%)] via-[hsla(220,55%,5%,0.05)] to-transparent pointer-events-none" />
    <div className="absolute inset-0 bg-[linear-gradient(105deg,hsla(220,60%,4%,0.35)_0%,transparent_55%)] pointer-events-none" />

    {/* ── Floating Instagram-style LIVE card · right side · syncs with active slide ── */}
    <div className="hidden lg:block absolute top-1/2 -translate-y-1/2 right-6 xl:right-12 z-20 w-[20rem] xl:w-[22rem]" aria-hidden="true, ">
     <div className="relative rounded-3xl bg-white shadow-[0_30px_80px_-20px_rgba(0,0,0,0.75)] ring-1 ring-black/10 rotate-[2.5deg] overflow-hidden">
      {/* Header, clean Instagram-style avatar (warm gradient ring + heart) */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-blue-950/8">
       <div className="w-10 h-10 rounded-full p-[2.5px] shrink-0" style={{ background: "conic-gradient(from 215deg, hsl(45,100%,65%), hsl(335,95%,60%), hsl(285,75%,55%), hsl(195,100%,60%), hsl(45,100%,65%))" }}>
        <div className="w-full h-full rounded-full bg-white flex items-center justify-center">
         <Heart className="w-4 h-4 text-pink-500" strokeWidth={2.4} fill="currentColor" />
        </div>
       </div>
       <div className="flex-1 min-w-0">
        <div className="text-blue-950 text-[13px] font-bold truncate leading-tight">@magic.cruises</div>
        <div className="text-blue-950/55 text-[10.5px] font-medium leading-tight mt-0.5">Sydney Harbour · Live</div>
       </div>
       <span className="text-blue-950/40 text-lg leading-none">···</span>
      </div>

      {/* Single photo, swaps & cross-fades per slide */}
      <div className="relative aspect-[4/5] overflow-hidden bg-blue-950/5">
       {HERO_SLIDES.map((s, i) => (
        <img
         key={i}
         src={s.img}
         alt={i === slideIdx ? s.alt : ""}
         className={`absolute inset-0 w-full h-full object-cover transition-all duration-[1000ms] ease-out ${i === slideIdx ? "opacity-100 scale-100" : "opacity-0 scale-105"}`}
         loading={i === 0 ? "eager" : "lazy"}
         aria-hidden={i === slideIdx ? "false, " : "true, "}
        />
       ))}
       {/* Per-slide category label bottom-left, subtle translucent dark pill */}
       <div key={`cat-${slideIdx}`} className="absolute bottom-3 left-3 px-3 py-1.5 rounded-md bg-blue-950/70 backdrop-blur-md text-white text-[10px] uppercase tracking-[0.3em] font-black shadow-lg ig-label-swap">
        {HERO_SLIDES[slideIdx].tag}
       </div>
       {/* LIVE pill bottom-right */}
       <div className="absolute bottom-3 right-3 px-3 py-1 rounded-full bg-white/95 text-blue-950 text-[10px] uppercase tracking-[0.28em] font-black shadow-lg">
        Live
       </div>
      </div>

      {/* Footer, icons + likes */}
      <div className="px-4 pt-3 pb-3.5">
       <div className="flex items-center gap-4 text-blue-950">
        <Heart className="w-5 h-5" strokeWidth={1.7} />
        <Wine className="w-5 h-5" strokeWidth={1.7} />
        <Sparkles className="w-5 h-5" strokeWidth={1.7} />
        <span className="ml-auto text-blue-950/55 text-[11px] font-semibold">2.4k likes</span>
       </div>
       <p key={`cap-${slideIdx}`} className="text-blue-950 text-[11.5px] mt-2 leading-snug ig-cap-swap">
        <strong className="font-bold">@magic.cruises</strong> <span className="text-blue-950/85">{HERO_SLIDES[slideIdx].quote}</span> <span className="text-pink-600 font-semibold">{HERO_SLIDES[slideIdx].hashtag}</span>
       </p>
      </div>
     </div>
    </div>

    {/* ── Instagram-story-style segmented progress bar, top of hero ── */}
    <div className="absolute top-24 lg:top-28 left-5 lg:left-10 right-5 lg:right-10 z-20 hidden md:flex gap-1.5" aria-label="Hero slide progress">
     {HERO_SLIDES.map((_, i) => (
      <div key={i} className="flex-1 h-[3px] rounded-full bg-white/15 overflow-hidden">
       <div className={`h-full bg-white transition-all ${i < slideIdx ? "w-full opacity-90" : i === slideIdx ? "w-full opacity-100 hero-progress-fill" : "w-0 opacity-0"}`} key={`${i}-${slideIdx}`} />
      </div>
     ))}
    </div>

    {/* ── Slide category indicator, top-right (hidden on lg since IG card takes that space) ── */}
    <div className="absolute top-32 right-4 z-20 hidden sm:flex lg:hidden items-center gap-3 px-4 py-2.5 rounded-full bg-black/40 backdrop-blur-md border border-white/15 shadow-2xl">
     <span className="w-1.5 h-1.5 rounded-full bg-amber-300 animate-pulse" aria-hidden="true, " />
     <span key={`tag-${slideIdx}`} className="text-white text-[11px] uppercase tracking-[0.32em] font-bold animate-fade-in">{HERO_SLIDES[slideIdx].tag}</span>
     <span className="text-white/40 text-[10px] uppercase tracking-[0.28em] font-bold tabular-nums">{String(slideIdx + 1).padStart(2, "0")} / {String(HERO_SLIDES.length).padStart(2, "0")}</span>
    </div>

    {/* ── Content, constrained to ~60% width on lg so the IG card has room ── */}
    <div className="relative mx-auto max-w-7xl w-full px-5 lg:px-10 z-10 lg:max-w-[60%] lg:mr-auto lg:ml-[max(1.25rem,calc((100vw-80rem)/2+2.5rem))]">
     <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 mb-7 lg:mb-9 animate-fade-in">
      <Sparkles className="w-3.5 h-3.5 text-amber-300" />
      <span className="text-[10px] sm:text-[11px] uppercase tracking-[0.32em] font-bold text-white">This is Magic</span>
     </div>

     <h1 className="font-display font-black tracking-tight text-white drop-shadow-[0_8px_32px_rgba(0,0,0,0.75)] max-w-[95vw] lg:max-w-full" style={{ fontSize: "clamp(2.25rem, 6.5vw, 5.75rem)", letterSpacing: "-0.02em", lineHeight: "0.95", overflowWrap: "anywhere" }} data-testid="home-h1">
      <span className="line-mask"><span style={{ "--line-delay": "0.05s" }}>Sydney Harbour</span></span>
      <span className="line-mask" style={{ marginTop: "0.04em" }}><span style={{ "--line-delay": "0.22s" }}>Cruise Charters</span></span>
      <span className="line-mask" style={{ marginTop: "0.02em" }}>
       <span key={`accent-${slideIdx}`} className="italic font-light hero-gradient-text" style={{ "--line-delay": "0.42s" }}>& private boat hire.</span>
      </span>
     </h1>

     {/* Per-slide italic quote, fades in fresh on each slide change (always warm yellow for premium feel) */}
     <div className="mt-5 h-7 lg:h-8 relative" aria-live="polite">
      <p key={`q-${slideIdx}`} className="absolute font-display italic text-lg sm:text-xl md:text-2xl tracking-tight slide-quote drop-shadow-[0_4px_16px_rgba(0,0,0,0.6)]" style={{ color: "hsl(48, 100%, 78%)" }}>
       "{HERO_SLIDES[slideIdx].quote}"
      </p>
     </div>

     <p className="text-white/92 text-base sm:text-lg md:text-xl lg:text-2xl mt-6 lg:mt-7 max-w-2xl leading-[1.55] font-light animate-fade-in" style={{ animationDelay: "0.85s" }}>
      Private Sydney Harbour cruises for <strong className="text-white font-semibold">birthdays, weddings, corporate events, networking functions, hens parties</strong> and unforgettable celebrations onboard <strong className="text-white font-semibold">The Magic</strong>.
     </p>

     {/* Category quick-jump pills, 4 themed gradient buttons */}
     <div className="flex flex-wrap gap-2.5 sm:gap-3 mt-6 lg:mt-7 animate-fade-in" style={{ animationDelay: "0.95s" }} data-testid="home-category-pills">
      {[
       { label: "Corporate", Icon: Briefcase, href: "/cruises/corporate-cruises-sydney-harbour", grad: "from-indigo-500 via-violet-500 to-blue-600", glow: "shadow-violet-500/40" },
       { label: "NYE & Vivid", Icon: PartyPopper, href: "/cruises/nye-cruises-sydney-harbour", grad: "from-fuchsia-500 via-purple-500 to-violet-600", glow: "shadow-fuchsia-500/40" },
       { label: "Birthdays", Icon: Cake, href: "/cruises/birthday-cruises-sydney", grad: "from-rose-500 via-pink-500 to-rose-600", glow: "shadow-pink-500/40" },
       { label: "Private", Icon: Heart, href: "/cruises/private-cruises-sydney-harbour", grad: "from-amber-400 via-orange-400 to-amber-500", glow: "shadow-amber-400/40" }].map((p, i) => (
       <Link key={i} to={p.href} className={`group inline-flex items-center gap-2 pl-3.5 pr-5 py-2.5 rounded-full bg-gradient-to-r ${p.grad} text-white text-[10px] sm:text-[11px] uppercase tracking-[0.24em] font-black shadow-lg ${p.glow} hover:scale-[1.04] hover:-translate-y-0.5 transition-all duration-300 ring-1 ring-white/15`}>
        <p.Icon className="w-3.5 h-3.5 opacity-95" />
        <span>{p.label}</span>
       </Link>
      ))}
     </div>

     <div className="flex flex-wrap gap-3 sm:gap-4 mt-5 lg:mt-7 animate-fade-in" style={{ animationDelay: "1.05s" }} data-testid="home-hero-ctas">
      <a href="#enquire" className="inline-flex items-center gap-3 px-7 sm:px-9 py-5 sm:py-6 rounded-full bg-amber-400 hover:bg-amber-300 text-blue-950 font-black text-[13px] sm:text-base tracking-widest uppercase shadow-2xl shadow-amber-500/40 transition-all hover:scale-105" data-testid="home-enquire-cta">
       Get a quote <ArrowRight className="w-5 h-5" />
      </a>
      <Link to="/cruises" className="inline-flex items-center gap-3 px-7 sm:px-9 py-5 sm:py-6 rounded-full bg-white/0 hover:bg-white/10 backdrop-blur-md text-white font-black text-[13px] sm:text-base tracking-widest uppercase border-2 border-white/50 hover:border-white/80 transition-all" data-testid="home-packages-cta">
       Explore Cruises
      </Link>
      <Link to={featuredTicket.href} className="inline-flex items-center gap-3 px-7 sm:px-9 py-5 sm:py-6 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md text-white font-black text-[13px] sm:text-base tracking-widest uppercase border border-white/30 transition-all" data-testid="home-featured-ticket-cta">
       <Ticket className="w-5 h-5" /> {featuredTicket.label}
      </Link>
      <Link to="/rates-and-menus" className="inline-flex items-center gap-3 px-7 sm:px-9 py-5 sm:py-6 rounded-full bg-transparent hover:bg-amber-400/10 text-amber-300 hover:text-amber-200 font-black text-[13px] sm:text-base tracking-widest uppercase border border-amber-300/50 hover:border-amber-300 transition-all" data-testid="home-hire-rates-cta">
       Magic hire rates
      </Link>
     </div>

     {/* Inline trust pills + slide dots, combined on one row */}
     <div className="flex flex-wrap items-center gap-x-6 gap-y-3 mt-7 lg:mt-9 animate-fade-in" style={{ animationDelay: "1.25s" }}>
      {[{ Icon: Star, l: "4.9★ · 2,500+ guests" },{ Icon: Award, l: "20 years" },{ Icon: Users, l: "Up to 120" },{ Icon: Anchor, l: "King St Wharf" }].map((f, i) => (
       <div key={i} className="flex items-center gap-2 text-white/85"><f.Icon className="w-4 h-4 text-amber-300" /><span className="text-[12px] sm:text-[13px] font-semibold">{f.l}</span></div>
      ))}
     </div>

     {/* Slide dots, moved to bottom-right corner of hero in the absolute layer below */}
    </div>

    {/* ── Slide dots, bottom-right corner ── */}
    <div className="absolute bottom-6 right-6 lg:bottom-8 lg:right-10 z-20 flex items-center gap-1.5" aria-label="Hero slide navigation">
     {HERO_SLIDES.map((s, i) => (
      <button
       key={i}
       type="button"
       onClick={() => setSlideIdx(i)}
       aria-label={`Show ${s.tag}`}
       className={`h-1.5 rounded-full transition-all duration-500 ${i === slideIdx ? "w-8 bg-amber-300" : "w-1.5 bg-white/35 hover:bg-white/70"}`}
      />
     ))}
    </div>

    {/* Scroll hint */}
    <div className="absolute bottom-7 left-6 lg:left-10 text-white/40 text-[10px] uppercase tracking-[0.32em] font-bold items-center gap-2 animate-fade-in hidden lg:flex" style={{ animationDelay: "1.5s" }}>
     <span className="w-10 h-px bg-amber-300/60" />
     <span>Scroll</span>
    </div>
   </section>

   {/* ════════ MARQUEE STRIP ════════ */}
   <section className="relative py-6 bg-[hsl(220,50%,6%)] border-y border-white/5 overflow-hidden">
    <div className="flex gap-12 ticker-track whitespace-nowrap text-white/30 text-2xl md:text-3xl font-display font-black uppercase tracking-tight">
     {Array.from({ length: 2 }).flatMap((_, i) => [
      <span key={`a-${i}`} className="flex items-center gap-12"><span>Sydney Harbour</span> <Sparkles className="w-5 h-5 text-amber-300" /> <span>Weddings</span> <Sparkles className="w-5 h-5 text-amber-300" /> <span>NYE Fireworks</span> <Sparkles className="w-5 h-5 text-amber-300" /> <span>Vivid Sydney</span> <Sparkles className="w-5 h-5 text-amber-300" /> <span>Birthdays</span> <Sparkles className="w-5 h-5 text-amber-300" /> <span>BYO Cruises</span> <Sparkles className="w-5 h-5 text-amber-300" /> </span>
     ])}
    </div>
   </section>

   {/* ════════ STATS STRIP, deep navy, yellow numerals (trust/credibility band) ════════ */}
   <section className="relative bg-[#0A1E3D] text-white overflow-hidden" data-testid="home-stats-band">
    <div className="absolute inset-0 pointer-events-none opacity-30" aria-hidden="true, ">
     <div className="absolute -top-32 left-1/4 w-[32rem] h-[32rem] rounded-full bg-blue-500/15 blur-3xl" />
     <div className="absolute -bottom-32 right-1/4 w-[28rem] h-[28rem] rounded-full bg-cyan-400/10 blur-3xl" />
    </div>
    <div className="relative max-w-7xl mx-auto px-5 lg:px-10 py-14 lg:py-20">
     <div className="grid grid-cols-2 lg:grid-cols-4 gap-y-10 lg:gap-y-0 lg:divide-x lg:divide-white/10">
      {[
       { num: "20+", label: "Years on Sydney Harbour" },
       { num: "5,000+", label: "Cruises hosted" },
       { num: "500+", label: "5-star reviews" },
       { num: "100", label: "Guest capacity" }].map((s, idx) => (
       <div key={idx} className="text-center px-4 lg:px-6">
        <div className="font-black text-amber-300 tracking-tight leading-none" style={{ fontSize: "clamp(2.5rem, 5.5vw, 5rem)", letterSpacing: "-0.03em" }}>{s.num}</div>
        <div className="mt-3 text-white/70 text-[11px] lg:text-xs uppercase tracking-[0.28em] font-semibold">{s.label}</div>
       </div>
      ))}
     </div>
    </div>
   </section>

   {/* ════════ WELCOME ABOARD · big hero photo + floating content card ════════ */}
   <section className="relative overflow-hidden text-white" data-testid="home-welcome-aboard" style={{ background: "linear-gradient(180deg, #06122B 0%, #0A1E3D 60%, #0A1E3D 100%)" }}>
    {/* Ambient orbs */}
    <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
     <div className="absolute -top-32 -left-32 w-[40rem] h-[40rem] rounded-full bg-amber-400/10 blur-3xl" />
     <div className="absolute -bottom-32 -right-20 w-[44rem] h-[44rem] rounded-full bg-sky-400/12 blur-3xl" />
    </div>

    <div className="relative max-w-[1500px] mx-auto px-4 lg:px-8 py-12 lg:py-16">
     {/* Eyebrow + headline above the photo, tightened */}
     <div className="max-w-3xl mb-6 lg:mb-8 reveal-up">
      <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.32em] text-amber-300 font-bold">
       <Crown className="w-3.5 h-3.5" /> Meet the Magic
      </div>
      <h2 className="mt-3 font-display font-black text-white text-3xl sm:text-4xl lg:text-5xl leading-[1.05] tracking-tight">
       Your floating venue.{" "}
       <span className="italic font-light text-amber-300">Crewed by humans who actually love this city.</span>
      </h2>
     </div>

     {/* Big photo with floating content card */}
     <div className="relative reveal-up">
      {/* outer glow */}
      <div className="absolute -inset-4 lg:-inset-8 rounded-[2.5rem] bg-gradient-to-br from-amber-300/30 via-sky-400/25 to-transparent blur-3xl" aria-hidden="true" />

      {/* gold ribbon badge */}
      <div className="absolute -top-4 left-6 lg:left-12 z-30 flex items-center gap-2 rounded-full bg-amber-300 text-[#0A1E3D] text-xs uppercase tracking-[0.24em] font-black px-5 py-2.5 shadow-2xl rotate-[-2deg]">
       <Anchor className="w-4 h-4" /> Welcome aboard
      </div>

      {/* hero photo */}
      <div className="relative rounded-[2rem] overflow-hidden ring-1 ring-white/15 shadow-[0_60px_140px_-30px_rgba(0,0,0,0.9)]">
       <img
        src="https://customer-assets.emergentagent.com/job_magic-harbour-party/artifacts/3au89qmo_sydney-magic-cruise-boat.jpg"
        alt="Magic Cruises hostess welcoming guests aboard the MV Sydney Magic on Sydney Harbour with the Opera House and Harbour Bridge in the background"
        width="2000"
        height="1400"
        loading="lazy"
        className="w-full h-[60vh] min-h-[480px] lg:h-[78vh] lg:min-h-[640px] xl:min-h-[720px] object-cover"
       />

       {/* gradient overlay for legibility */}
       <div className="absolute inset-0 bg-gradient-to-r from-[#0A1E3D]/85 via-[#0A1E3D]/35 to-transparent lg:from-[#0A1E3D]/80 lg:via-transparent lg:to-transparent" />
       <div className="absolute inset-0 bg-gradient-to-t from-[#06122B]/90 via-transparent to-transparent" />

       {/* floating content card overlay, bottom-left */}
       <div className="absolute inset-x-4 bottom-4 lg:inset-x-auto lg:left-10 lg:bottom-10 lg:max-w-xl xl:max-w-2xl">
        <div className="rounded-2xl bg-[#06122B]/85 backdrop-blur-xl ring-1 ring-white/15 p-6 lg:p-8 shadow-2xl">
         <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] text-amber-300 font-bold">
          <Sparkles className="w-3.5 h-3.5" /> MV Sydney Magic &middot; Since 2006
         </div>
         <p className="mt-4 text-white/85 text-base lg:text-lg leading-relaxed">
          Step aboard Magic, our private 120-guest Sydney Harbour cruise boat,
          perfect for birthdays, corporate events, weddings and all special
          occasions. With an air-conditioned saloon, spacious outdoor deck,
          licensed bar and dance floor, Magic offers an unforgettable private
          charter experience on Sydney Harbour.
         </p>

         {/* feature chips */}
         <div className="mt-5 grid grid-cols-2 gap-2.5">
          {[
           { icon: Anchor, label: "Private Harbour Charters" },
           { icon: Users, label: "120 Guest Cruise Boat" },
           { icon: Wine, label: "Licensed Bar &amp; Dance Floor" },
           { icon: Briefcase, label: "Corporate &amp; Private Events" },
          ].map((f, i) => (
           <div key={i} className="flex items-center gap-2.5 rounded-xl bg-white/5 ring-1 ring-white/10 px-3.5 py-2.5">
            <f.icon className="w-4 h-4 text-amber-300 shrink-0" />
            <div className="text-white text-xs lg:text-sm font-semibold leading-tight" dangerouslySetInnerHTML={{ __html: f.label }} />
           </div>
          ))}
         </div>

         {/* CTAs */}
         <div className="mt-6 flex flex-wrap gap-2.5">
          <Link
           to="/boat"
           data-testid="welcome-aboard-tour-btn"
           className="group inline-flex items-center gap-2 rounded-full bg-amber-300 hover:bg-amber-200 text-[#0A1E3D] font-black uppercase tracking-[0.18em] text-xs px-6 py-3.5 shadow-[0_10px_30px_-10px_rgba(252,211,77,0.7)] transition-all hover:scale-[1.02]"
          >
           Tour the Magic <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
          <Link
           to="/gallery"
           data-testid="welcome-aboard-gallery-btn"
           className="inline-flex items-center gap-2 rounded-full bg-white/5 hover:bg-white/10 text-white font-bold uppercase tracking-[0.18em] text-xs px-6 py-3.5 ring-1 ring-white/15 transition-colors"
          >
           <Camera className="w-4 h-4" /> See it in action
          </Link>
         </div>
        </div>
       </div>

       {/* top-right star badge floating on photo */}
       <div className="hidden lg:flex absolute top-6 right-6 z-20 items-center gap-2 rounded-full bg-white/95 backdrop-blur px-4 py-2 shadow-xl">
        <div className="flex items-center gap-0.5 text-amber-500">
         {[0,1,2,3,4].map(i => <Star key={i} className="w-3.5 h-3.5 fill-current" />)}
        </div>
        <div className="text-[#0A1E3D] text-xs font-black tracking-wide">4.9 &middot; 500+ reviews</div>
       </div>
      </div>
     </div>
    </div>
   </section>

   {/* ════════ SECTION 01, ONE BOAT. EVERY EVENT MADE MAGICAL. · editorial split + SEO copy band ════════ */}
   <section className="relative overflow-hidden text-[#0A1E3D] bg-white" data-testid="home-one-boat">
    {/* Background, soft gradient + subtle decorative orbs */}
    <div className="absolute inset-0 pointer-events-none" aria-hidden="true, ">
     <div className="absolute -top-32 -right-20 w-[36rem] h-[36rem] rounded-full bg-blue-100/50 blur-3xl" />
     <div className="absolute -bottom-40 -left-20 w-[32rem] h-[32rem] rounded-full bg-sky-100/50 blur-3xl" />
    </div>

    <div className="relative mx-auto max-w-7xl px-5 lg:px-10 py-24 lg:py-32">
     {/* ── PRIMARY SPLIT · left typography + CTA, right big cinematic boat image with overlap ── */}
     <div className="grid lg:grid-cols-12 gap-10 lg:gap-20 items-center">
      {/* LEFT, oversized typography + supporting copy + CTA */}
      <div className="lg:col-span-5 relative z-10">
       <div className="text-[#0A1E3D]/60 text-[11px] uppercase tracking-[0.32em] font-bold inline-flex items-center gap-3 mb-7">
        <span className="w-8 h-px bg-[#0A1E3D]/40" />
        Section 01 · The boat
       </div>
       <h2 className="text-[#0A1E3D] font-black tracking-tight leading-[0.9]" style={{ fontSize: "clamp(2.75rem, 6.5vw, 6rem)", letterSpacing: "-0.028em" }}>
        One boat.<br />
        <span className="italic font-normal text-blue-600">Every event</span><br />
        made magical.
       </h2>
       <p className="text-[#0A1E3D]/70 text-lg lg:text-xl leading-[1.7] mt-8 max-w-md">
        Two decks, panoramic harbour views, a licensed bar and a crew that's been running Sydney Harbour celebrations for over 20 years. From intimate engagements to 100-guest takeovers.
       </p>
       <div className="mt-10 flex flex-wrap items-center gap-4">
        <Link to="/boat" className="inline-flex items-center gap-2.5 bg-[#0A1E3D] text-white px-7 py-4 rounded-full font-black text-sm tracking-wide hover:bg-blue-700 transition-colors">
         Explore the boat
         <ArrowRight className="w-4 h-4" />
        </Link>
        <Link to="#enquire" className="inline-flex items-center gap-2 text-[#0A1E3D] text-sm uppercase tracking-[0.28em] font-black border-b-2 border-[#0A1E3D] pb-1 hover:border-blue-600 hover:text-blue-600 transition-colors">
         Enquire now <ArrowRight className="w-4 h-4" />
        </Link>
       </div>
      </div>

      {/* RIGHT, big cinematic boat image, slightly overlapping outside the container + floating stat badges */}
      <div className="lg:col-span-7 relative">
       <div className="relative rounded-[2rem] overflow-hidden ring-1 ring-blue-100 shadow-[0_40px_90px_-30px_rgba(10,30,61,0.45)] aspect-[5/6] lg:aspect-[6/7] lg:-mr-12 xl:-mr-20">
        <img src={MAGIC_PHOTOS.bridgeFamilyGroup} alt="MV Sydney Magic on the harbour with Bridge and Opera House" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#0A1E3D]/30 via-transparent to-transparent" />
       </div>

       {/* Floating stat card 1, top-left, overlapping outside */}
       <div className="hidden md:block absolute -top-6 -left-6 lg:-left-12 bg-white rounded-2xl p-5 pr-7 shadow-[0_20px_50px_-12px_rgba(10,30,61,0.25)] ring-1 ring-blue-100 z-20">
        <div className="flex items-center gap-3">
         <div className="w-10 h-10 rounded-full bg-amber-300 inline-flex items-center justify-center">
          <Users className="w-5 h-5 text-[#0A1E3D]" />
         </div>
         <div>
          <div className="text-[#0A1E3D] font-black text-2xl leading-none">100</div>
          <div className="text-[#0A1E3D]/60 text-[10px] uppercase tracking-[0.24em] font-bold mt-1">Guests · seated or standing</div>
         </div>
        </div>
       </div>

       {/* Floating stat card 2, bottom-right, overlapping outside */}
       <div className="hidden md:block absolute -bottom-6 -right-6 lg:-right-4 bg-white rounded-2xl p-5 pr-7 shadow-[0_20px_50px_-12px_rgba(10,30,61,0.25)] ring-1 ring-blue-100 z-20">
        <div className="flex items-center gap-3">
         <div className="w-10 h-10 rounded-full bg-blue-600 inline-flex items-center justify-center">
          <Anchor className="w-5 h-5 text-white" />
         </div>
         <div>
          <div className="text-[#0A1E3D] font-black text-2xl leading-none">2</div>
          <div className="text-[#0A1E3D]/60 text-[10px] uppercase tracking-[0.24em] font-bold mt-1">Full decks · indoor + open</div>
         </div>
        </div>
       </div>

       {/* Floating stat card 3, middle-left badge */}
       <div className="hidden lg:block absolute top-1/2 -left-10 -translate-y-1/2 bg-[#0A1E3D] text-white rounded-2xl px-5 py-4 shadow-[0_20px_50px_-12px_rgba(10,30,61,0.4)] z-20">
        <div className="flex items-center gap-2">
         <Star className="w-4 h-4 text-amber-300" fill="currentColor" strokeWidth={0} />
         <div className="text-[10px] uppercase tracking-[0.28em] font-black">500+ 5-star reviews</div>
        </div>
       </div>
      </div>
     </div>

    </div>
   </section>


   {/* ════════ TICKETED CRUISES, Vivid · NYE · Australia Day · Boxing Day ════════ */}
   <section className="relative overflow-hidden text-white" data-testid="home-ticketed" style={{ background: "radial-gradient(at 20% 10%, hsl(280,60%,15%) 0%, transparent 55%), radial-gradient(at 85% 90%, hsl(220,70%,10%) 0%, transparent 55%), linear-gradient(180deg, hsl(225,70%,7%) 0%, hsl(265,55%,9%) 100%)" }}>
    {/* Atmospheric glow orbs */}
    <div className="absolute inset-0 pointer-events-none" aria-hidden="true, ">
     <div className="absolute top-[10%] left-[15%] w-[36rem] h-[36rem] rounded-full bg-fuchsia-500/12 blur-3xl" />
     <div className="absolute top-[40%] right-[10%] w-[32rem] h-[32rem] rounded-full bg-amber-400/10 blur-3xl" />
     <div className="absolute bottom-[5%] left-[40%] w-[28rem] h-[28rem] rounded-full bg-cyan-400/8 blur-3xl" />
    </div>

    <div className="relative mx-auto max-w-7xl px-5 lg:px-10 py-24 lg:py-32">
     {/* Section header */}
     <div className="grid lg:grid-cols-12 gap-10 items-end mb-14 lg:mb-20">
      <div className="lg:col-span-7">
       <div className="text-amber-300 text-[11px] uppercase tracking-[0.32em] font-bold inline-flex items-center gap-3 mb-6">
        <span className="w-8 h-px bg-amber-300/60" />
        <Sparkles className="w-3.5 h-3.5" fill="currentColor" strokeWidth={0} />
        Ticketed Cruises · Buy per seat
       </div>
       <h2 className="text-white font-black tracking-tight leading-[0.95]" style={{ fontSize: "clamp(2.25rem, 5.5vw, 4.75rem)", letterSpacing: "-0.025em" }}>
        Sydney's iconic dates.{" "}
        <span className="italic font-light bg-clip-text text-transparent inline-block pr-2 pb-3 -mr-2 leading-[1.1]" style={{ backgroundImage: "linear-gradient(90deg, hsl(50,100%,72%) 0%, hsl(320,90%,72%) 50%, hsl(280,90%,75%) 100%)" }}>
         Tickets selling fast.
        </span>
       </h2>
       <p className="text-white/75 mt-6 text-base md:text-lg leading-[1.7] max-w-2xl">
        Four nights on the calendar where Sydney Harbour is the only place to be. Per-ticket boarding, public cruises, premium drinks on arrival, no charter required.
       </p>
      </div>

      {/* RIGHT, Vivid-style lit countdown card · animated lights fill the dead space */}
      <div className="lg:col-span-5">
       <div className="relative rounded-[1.75rem] overflow-hidden ring-1 ring-fuchsia-400/25 backdrop-blur-xl p-6 lg:p-7" style={{ background: "linear-gradient(135deg, hsla(280,60%,15%,0.7) 0%, hsla(220,60%,10%,0.7) 100%)" }}>
        {/* Animated laser-beam atmosphere */}
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true, ">
         <div className="absolute -top-20 -right-10 w-72 h-72 rounded-full bg-fuchsia-500/25 blur-3xl animate-pulse" style={{ animationDuration: "4s" }} />
         <div className="absolute -bottom-16 -left-12 w-56 h-56 rounded-full bg-amber-400/20 blur-3xl animate-pulse" style={{ animationDuration: "5s", animationDelay: "1s" }} />
         <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-40 h-40 rounded-full bg-cyan-400/15 blur-3xl animate-pulse" style={{ animationDuration: "6s", animationDelay: "2s" }} />
         {/* Diagonal "laser" beams */}
         <div className="absolute -top-10 left-1/4 w-px h-[140%] bg-gradient-to-b from-transparent via-fuchsia-300/40 to-transparent rotate-12" />
         <div className="absolute -top-10 right-1/3 w-px h-[140%] bg-gradient-to-b from-transparent via-amber-300/30 to-transparent -rotate-12" />
         <div className="absolute -top-10 right-1/4 w-px h-[140%] bg-gradient-to-b from-transparent via-cyan-300/30 to-transparent rotate-6" />
        </div>

        <div className="relative">
         {/* Top label */}
         <div className="flex items-center justify-between mb-5">
          <div className="text-amber-300 text-[10px] uppercase tracking-[0.32em] font-bold inline-flex items-center gap-3">
           <span className="w-6 h-px bg-amber-300/60" /> Up next
          </div>
          <div className="inline-flex items-center gap-1.5 text-[9px] uppercase tracking-[0.28em] font-black bg-fuchsia-500 text-white px-2.5 py-1 rounded-full shadow-[0_0_20px_rgba(217,70,239,0.5)]">
           <span className="relative flex items-center">
            <span className="w-1.5 h-1.5 rounded-full bg-white" />
            <span className="absolute inset-0 w-1.5 h-1.5 rounded-full bg-white animate-ping opacity-70" />
           </span>
           Selling fast
          </div>
         </div>

         {/* Stat row · the big number */}
         <div className="flex items-baseline gap-3 mb-1">
          <div className="font-black text-white tracking-tight leading-none" style={{ fontSize: "clamp(3rem, 5vw, 4.5rem)", letterSpacing: "-0.04em" }}>04</div>
          <div className="font-black text-fuchsia-300 text-2xl tracking-tight">nights</div>
         </div>
         <p className="text-white/65 text-[13px] leading-snug mb-5">that always sell out. Lock yours before they're gone.</p>

         {/* Mini event list, replaces dead space with real info */}
         <ul className="space-y-2.5">
          <li className="flex items-center justify-between gap-3 rounded-xl bg-white/[0.06] ring-1 ring-white/10 px-3.5 py-2.5">
           <div className="flex items-center gap-3 min-w-0">
            <Sparkles className="w-3.5 h-3.5 text-fuchsia-300 shrink-0" fill="currentColor" strokeWidth={0} />
            <div className="text-white font-black text-[13px] tracking-tight truncate">Vivid Sydney</div>
           </div>
           <div className="text-white/55 text-[10px] uppercase tracking-[0.24em] font-bold whitespace-nowrap">28 May → 19 Jun</div>
          </li>
          <li className="flex items-center justify-between gap-3 rounded-xl bg-white/[0.06] ring-1 ring-white/10 px-3.5 py-2.5">
           <div className="flex items-center gap-3 min-w-0">
            <Star className="w-3.5 h-3.5 text-amber-300 shrink-0" fill="currentColor" strokeWidth={0} />
            <div className="text-white font-black text-[13px] tracking-tight truncate">New Year's Eve</div>
           </div>
           <div className="text-white/55 text-[10px] uppercase tracking-[0.24em] font-bold whitespace-nowrap">31 Dec</div>
          </li>
          <li className="flex items-center justify-between gap-3 rounded-xl bg-white/[0.06] ring-1 ring-white/10 px-3.5 py-2.5">
           <div className="flex items-center gap-3 min-w-0">
            <PartyPopper className="w-3.5 h-3.5 text-cyan-300 shrink-0" />
            <div className="text-white font-black text-[13px] tracking-tight truncate">Australia Day</div>
           </div>
           <div className="text-white/55 text-[10px] uppercase tracking-[0.24em] font-bold whitespace-nowrap">26 Jan</div>
          </li>
         </ul>

         {/* Departure note */}
         <div className="mt-5 pt-4 border-t border-white/10 flex items-center gap-2 text-white/55 text-[10px] uppercase tracking-[0.28em] font-bold">
          <MapPin className="w-3 h-3 text-amber-300" /> Per-ticket boarding · King St Wharf
         </div>
        </div>
       </div>
      </div>
     </div>

     {/* Cards grid · order driven by admin via /api/cms/home-order */}
     <HomeTicketedCards />

     {/* Footer microcopy + trust */}
     <div className="mt-12 lg:mt-14 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5 pt-8 border-t border-white/10">
      <div className="text-white/55 text-xs lg:text-sm leading-relaxed">
       Per-ticket public cruises. Boarding from <strong className="text-white">King Street Wharf, Darling Harbour</strong>. Private charters also available on every iconic date.
      </div>
      <Link to="/availability" className="inline-flex items-center gap-2 text-amber-300 hover:text-amber-200 text-[11px] uppercase tracking-[0.32em] font-black self-start sm:self-auto" data-testid="ticketed-check-availability">
       Check live availability <ArrowRight className="w-3.5 h-3.5" />
      </Link>
     </div>

    </div>
   </section>



   {/* ════════ SOCIAL DIARY, HORIZONTAL POLAROID STRIP (warm cream) ════════ */}
   <section className="relative py-20 lg:py-24 overflow-visible bg-[hsl(35,42%,95%)] text-blue-950" data-testid="home-social-diary">
    {/* Floating overlap, image bleeds out of the section into the dark above (asymmetric, off-center) */}
    <div className="hidden md:block absolute -top-20 lg:-top-28 right-8 lg:right-24 w-44 lg:w-56 rotate-[-7deg] z-30 pointer-events-none">
     <div className="relative rounded-2xl overflow-hidden ring-1 ring-white/20 shadow-[0_20px_50px_-12px_rgba(0,0,0,0.6)] bg-white p-2.5 pb-5">
      <div className="aspect-[4/5] overflow-hidden rounded-lg">
       <img src={MAGIC_PHOTOS.championToast} alt="Candid sunset toast on Magic" className="w-full h-full object-cover" loading="lazy" />
      </div>
      <div className="text-blue-950 text-[11px] font-display italic text-center mt-2.5">friday · sunset toast</div>
     </div>
    </div>
    <div className="absolute inset-0 pointer-events-none opacity-50">
     <div className="absolute -top-20 left-1/4 w-[36rem] h-[36rem] rounded-full bg-amber-300/30 blur-3xl" />
     <div className="absolute -bottom-20 right-1/4 w-[32rem] h-[32rem] rounded-full bg-rose-300/25 blur-3xl" />
    </div>
    <div className="relative mx-auto max-w-7xl px-5 lg:px-10 mb-12">
     <div className="flex items-end justify-between gap-6 flex-wrap">
      <div>
       <div className="text-pink-600 text-[11px] uppercase tracking-[0.32em] font-bold mb-3 flex items-center gap-2"><Heart className="w-3.5 h-3.5" /> @magic.cruises</div>
       <h2 className="font-display font-black tracking-tight leading-[0.92]" style={{ fontSize: "clamp(2.25rem, 5.5vw, 4.5rem)", letterSpacing: "-0.02em" }}>Magical Harbour <span className="italic font-light text-pink-600">Highlights.</span></h2>
      </div>
     </div>
    </div>
    <div className="relative overflow-x-auto pb-6 scroll-smooth scrollbar-hide">
     <div className="flex gap-5 px-5 lg:px-10 min-w-min">
      {[
       { img: MAGIC_PHOTOS.weddingBrideGroomSunset, cap: "Wedding Cruises" },
       { img: MAGIC_PHOTOS.engagementSunsetProposal, cap: "Engagement · Sunset Proposal" },
       { img: MAGIC_PHOTOS.vividMagicBoat, cap: "Vivid · Bridge in neon" },
       { img: MAGIC_PHOTOS.hensPinkParty, cap: "Bride to Be Party" },
       { img: MAGIC_PHOTOS.corporateChampagne, cap: "Corporate Function" },
       { img: MAGIC_PHOTOS.byoSunsetDeck, cap: "BYO Party" },
       { img: MAGIC_PHOTOS.familyBowSunset, cap: "Three generations · bow" },
       { img: MAGIC_PHOTOS.womensGroupChampagne, cap: "Sydney Harbour cruises · Women's Group" },
      { img: MAGIC_PHOTOS.nyeFireworksHero, cap: "NYE · front row" }].map((p, i) => (
       <div key={i} className={`shrink-0 w-60 md:w-72 rounded-2xl bg-white shadow-2xl shadow-blue-950/15 ${i % 2 === 0 ? "rotate-[-2deg]" : "rotate-[2deg]"} hover:rotate-0 hover:scale-105 transition-transform duration-500 p-3 pb-6`}>
        <div className="aspect-square overflow-hidden rounded-lg"><img src={p.img} alt={p.cap} loading="lazy" className="w-full h-full object-cover" /></div>
        <div className="text-blue-950 text-sm font-bold mt-4 text-center font-display italic">{p.cap}</div>
       </div>
      ))}
     </div>
    </div>
   </section>

   {/* ════════ FULL-BLEED IMAGE BREAK 1, EDITORIAL QUOTE ════════ */}
   <section className="relative h-[95vh] min-h-[700px] overflow-hidden grain-noise">
    <img src={MAGIC_PHOTOS.girlsChampagneBridge} alt="Sydney Harbour Cruises for Women In Business · celebration on the deck of MV Sydney Magic · Sydney Harbour boat cruises for International Women's Day" className="absolute inset-0 w-full h-full object-cover object-top animate-vivid-pan" />
    <div className="absolute inset-0 bg-gradient-to-r from-[hsla(220,60%,4%,0.92)] via-[hsla(220,60%,4%,0.35)] to-[hsla(220,60%,4%,0.15)]" />
    <div className="absolute inset-0 flex items-center">
     <div className="relative mx-auto max-w-7xl w-full px-5 lg:px-10">
      <div className="max-w-3xl reveal-up">
       <div className="text-amber-300 text-[11px] uppercase tracking-[0.32em] font-bold mb-6">In the spotlight</div>
       <p className="text-white text-4xl md:text-6xl lg:text-7xl font-display font-black leading-[1.02] tracking-tight drop-shadow-[0_8px_32px_rgba(0,0,0,0.6)]" style={{ letterSpacing: "-0.02em" }}>Harbour Celebrations <em className="not-italic text-amber-300">For Women In Business.</em></p>
       <div className="text-white/88 text-sm mt-7 uppercase tracking-[0.32em] font-bold">, International Women's Day · 8 March 2027</div>
      </div>
     </div>
    </div>
   </section>

   {/* ── Section divider · sparkle band ── */}
   <div className="relative bg-[hsl(220,55%,5%)] py-10 lg:py-14 overflow-hidden" aria-hidden="true, ">
    {/* Drifting sparkles, soft golden + fuchsia twinkles */}
    <div className="absolute inset-0 pointer-events-none">
     {Array.from({ length: 14 }).map((_, i) => {
      const left = 5 + (i * 7) + (Math.random() * 3);
      const top = 30 + Math.random() * 40;
      const size = 8 + Math.random() * 14;
      const delay = -Math.random() * 4;
      const colors = ["text-amber-300", "text-fuchsia-300", "text-pink-300", "text-white"];
      const color = colors[i % colors.length];
      return (
       <Sparkles key={i} className={`absolute ${color} divider-sparkle`} style={{ left: `${left}%`, top: `${top}%`, width: size, height: size, animationDelay: `${delay}s`, opacity: 0.5 + Math.random() * 0.45 }} fill="currentColor" strokeWidth={0} />
      );
     })}
    </div>
    {/* Center editorial divider line + center sparkle medallion */}
    <div className="relative max-w-7xl mx-auto px-6 flex items-center justify-center gap-5">
     <span className="hidden sm:block flex-1 h-px bg-gradient-to-r from-transparent via-amber-300/40 to-fuchsia-400/60" />
     <div className="flex items-center gap-3 px-5 py-2.5 rounded-full bg-white/[0.04] backdrop-blur-sm border border-white/15">
      <Sparkles className="w-4 h-4 text-amber-300" fill="currentColor" strokeWidth={0} />
      <span className="text-white/85 text-[10px] uppercase tracking-[0.42em] font-black">A night on Magic</span>
      <Sparkles className="w-4 h-4 text-fuchsia-300" fill="currentColor" strokeWidth={0} />
     </div>
     <span className="hidden sm:block flex-1 h-px bg-gradient-to-r from-fuchsia-400/60 via-amber-300/40 to-transparent" />
    </div>
   </div>

   {/* ════════ SEO EDITORIAL · WHY SYDNEY HARBOUR, magazine spread with 4 unique photos ════════ */}
   <section className="relative overflow-hidden text-blue-950 bg-[hsl(35,42%,95%)]" data-testid="home-seo-destination">
    <div className="absolute inset-0 pointer-events-none opacity-50" aria-hidden="true, ">
     <div className="absolute -top-32 left-1/3 w-[40rem] h-[40rem] rounded-full bg-amber-200/40 blur-3xl" />
     <div className="absolute -bottom-32 right-1/3 w-[36rem] h-[36rem] rounded-full bg-rose-200/30 blur-3xl" />
    </div>

    {/* === HEADER · merged About Magic Cruises + Setting · richer split layout === */}
    <div className="relative mx-auto max-w-7xl px-5 lg:px-10 pt-20 lg:pt-28 pb-12 lg:pb-16">
     <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
      <div className="lg:col-span-6">
       <div className="text-amber-700 text-[11px] uppercase tracking-[0.32em] font-bold inline-flex items-center gap-3 mb-6">
        <span className="w-8 h-px bg-amber-600/60" /> About Magic Cruises · Sydney Harbour
       </div>
       <h2 className="text-blue-950 font-black tracking-tight leading-[0.92]" style={{ fontSize: "clamp(2.25rem, 5vw, 4.25rem)", letterSpacing: "-0.025em" }}>
        One of the most recognised destinations <span className="italic font-light text-pink-600">in the world.</span>
       </h2>
       <p className="text-blue-950/75 text-base md:text-lg leading-[1.75] mt-7">
        Sunsets behind the Opera House. The Harbour Bridge lit gold. A skyline that turns every cruise into a story. There's no better way to experience Sydney Harbour than onboard a private cruise surrounded by incredible harbour views, good company, music, food and drinks.
       </p>
       <p className="text-blue-950/70 text-[15px] md:text-[16px] leading-[1.85] mt-5">
        Magic Cruises specialises in Sydney Harbour cruises, private boat hire Sydney, harbour cruise charters and private event cruises for groups looking to celebrate on the water, from relaxed daytime harbour cruises through to evening party boat charters across Sydney Harbour.
       </p>
       <div className="mt-8 flex flex-wrap items-center gap-3">
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/80 ring-1 ring-amber-700/15 text-amber-800 text-[10px] uppercase tracking-[0.24em] font-black">Birthdays</span>
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/80 ring-1 ring-amber-700/15 text-rose-700 text-[10px] uppercase tracking-[0.24em] font-black">Weddings</span>
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/80 ring-1 ring-amber-700/15 text-blue-700 text-[10px] uppercase tracking-[0.24em] font-black">Corporate</span>
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/80 ring-1 ring-amber-700/15 text-fuchsia-700 text-[10px] uppercase tracking-[0.24em] font-black">Networking</span>
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/80 ring-1 ring-amber-700/15 text-pink-700 text-[10px] uppercase tracking-[0.24em] font-black">Hens & Parties</span>
       </div>
       <div className="mt-8 flex items-center gap-4 text-blue-950/55 text-[11px] uppercase tracking-[0.32em] font-bold">
        <div className="w-10 h-px bg-blue-950/30" />
        <span>King St Wharf · Darling Harbour</span>
       </div>
      </div>
      <div className="lg:col-span-6">
       <Lightbox src="https://customer-assets.emergentagent.com/job_magic-harbour-party/artifacts/e163vp58_magic-cruise-sydney-harbour.jpg" alt="Sydney Harbour sunset cruise group on the bow of the Magic with Opera House, Harbour Bridge and city skyline silhouetted against a golden sun">
        <div className="relative rounded-[2rem] overflow-hidden ring-1 ring-amber-900/15 shadow-[0_40px_90px_-30px_rgba(0,0,0,0.4)] aspect-[4/5] lg:aspect-[5/6] hover:shadow-[0_50px_110px_-30px_rgba(0,0,0,0.5)] transition-shadow">
         <img src="https://customer-assets.emergentagent.com/job_magic-harbour-party/artifacts/e163vp58_magic-cruise-sydney-harbour.jpg" alt="Sydney Harbour sunset cruise group on the bow of the Magic with Opera House, Harbour Bridge and city skyline silhouetted against a golden sun" loading="lazy" className="absolute inset-0 w-full h-full object-cover object-top hover:scale-105 transition-transform duration-[1500ms]" />
         <div className="absolute inset-0 bg-gradient-to-t from-amber-950/45 via-transparent to-transparent" />
         {/* Bottom-left caption pill */}
         <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between gap-3">
          <div className="text-white">
           <div className="text-amber-200 text-[10px] uppercase tracking-[0.32em] font-black mb-1">Sunset on the Magic</div>
           <div className="text-base lg:text-lg font-black tracking-tight drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]">Opera House · Harbour Bridge · CBD</div>
          </div>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/95 text-amber-800 text-[10px] uppercase tracking-[0.22em] font-black backdrop-blur shrink-0">
           <Camera className="w-3 h-3" /> Click
          </span>
         </div>
        </div>
       </Lightbox>
      </div>
     </div>
    </div>

    {/* === EDITORIAL BODY, magazine 12-col grid · text + interspersed photo cards === */}
    <div className="relative mx-auto max-w-7xl px-5 lg:px-10 pb-20 lg:pb-28">
     <div className="grid lg:grid-cols-12 gap-10 lg:gap-14">

      {/* LEFT COLUMN, first 2 paragraphs */}
      <div className="lg:col-span-5 space-y-6">
       <div className="text-pink-600 text-[10px] uppercase tracking-[0.32em] font-black mb-1">Why the harbour wins</div>
       <p className="text-blue-950/85 text-[17px] leading-[1.85]">
        Cruising past the Sydney Opera House, Harbour Bridge, Luna Park and the city skyline creates an atmosphere that traditional event venues simply cannot match. Whether you're planning a birthday celebration, corporate event, wedding reception, engagement party or networking function, every kind of celebration finds its rhythm on the water.
       </p>
       <p className="text-blue-950/85 text-[17px] leading-[1.85]">
        Unlike public cruise operators, Magic Cruises focuses entirely on private Sydney Harbour boat hire and private charter cruises. Every cruise onboard The Magic is exclusive to your group, giving guests the freedom to enjoy their own private harbour event without sharing the experience with strangers.
       </p>
      </div>

      {/* RIGHT COLUMN, Boxing Day photo (large feature) */}
      <div className="lg:col-span-7">
       <Lightbox src="https://customer-assets.emergentagent.com/job_magic-harbour-party/artifacts/m3txpnpd_boxing-day-cruises-sydney-harbour-magic.jpg" alt="Boxing Day cruise on Sydney Harbour · Sydney to Hobart yacht race start · crowd watching from the Magic's deck">
        <div className="relative rounded-3xl overflow-hidden ring-1 ring-blue-900/10 shadow-[0_30px_70px_-25px_rgba(0,0,0,0.35)] aspect-[16/10] lg:aspect-[5/3] hover:shadow-[0_40px_90px_-25px_rgba(0,0,0,0.45)] transition-shadow">
         <img src="https://customer-assets.emergentagent.com/job_magic-harbour-party/artifacts/m3txpnpd_boxing-day-cruises-sydney-harbour-magic.jpg" alt="Boxing Day cruise on Sydney Harbour · Sydney to Hobart yacht race start · crowd watching from the Magic's deck" loading="lazy" className="absolute inset-0 w-full h-full object-cover hover:scale-105 transition-transform duration-1000" />
         <div className="absolute inset-0 bg-gradient-to-t from-blue-950/35 via-transparent to-transparent" />
         <div className="absolute bottom-5 left-5 flex items-center gap-2.5">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-300 text-emerald-950 text-[10px] uppercase tracking-[0.24em] font-black backdrop-blur">Boxing Day · Sydney to Hobart</span>
         </div>
        </div>
       </Lightbox>
       {/* Caption */}
       <div className="mt-4 text-blue-950/55 text-[11px] uppercase tracking-[0.28em] font-black">
        <span className="text-emerald-700">↳</span> Front-row to the race start · 26 December
       </div>
      </div>

      {/* SECOND ROW, 16th birthday photo (asymmetric small) + body text + Happy Birthday large */}
      <div className="lg:col-span-4 lg:order-1 order-2">
       <Lightbox src="https://customer-assets.emergentagent.com/job_magic-harbour-party/artifacts/n2oh3ova_birthday-cruises-sydney-harbour-magi.jpg" alt="16th birthday styling on the Magic · Happy Birthday Siicra balloon arch · teal, silver and starburst decorations · marquee number 16 with harbour views">
        <div className="relative rounded-3xl overflow-hidden ring-1 ring-blue-900/10 shadow-lg aspect-[4/5] hover:shadow-xl transition-shadow">
         <img src="https://customer-assets.emergentagent.com/job_magic-harbour-party/artifacts/n2oh3ova_birthday-cruises-sydney-harbour-magi.jpg" alt="16th birthday styling on the Magic · Happy Birthday Siicra balloon arch · teal, silver and starburst decorations · marquee number 16 with harbour views" loading="lazy" className="absolute inset-0 w-full h-full object-cover hover:scale-105 transition-transform duration-1000" />
         <div className="absolute inset-0 bg-gradient-to-t from-blue-950/45 via-transparent to-transparent" />
         <div className="absolute bottom-5 left-5 right-5">
          <div className="text-teal-200 text-[10px] uppercase tracking-[0.32em] font-black mb-1.5">Sweet 16 · Styled</div>
          <div className="text-white text-lg font-black tracking-tight leading-tight">Balloons, marquee 16 and Sydney lights through the windows.</div>
         </div>
        </div>
       </Lightbox>
      </div>

      <div className="lg:col-span-8 lg:order-2 order-1 lg:pl-6 lg:border-l border-blue-950/10 space-y-6">
       <div className="text-amber-700 text-[10px] uppercase tracking-[0.32em] font-black">From iconic dates to private milestones</div>
       <p className="text-blue-950/85 text-[17px] leading-[1.85]">
        This private charter experience has made Magic Cruises one of the most popular choices for Sydney Harbour cruise charters, party boat hire Sydney and private harbour events. Private boat hire on Sydney Harbour continues to grow in popularity because people want more than a standard venue. Guests are looking for memorable experiences, unique event spaces and exciting harbour cruise venues that combine entertainment, scenery and atmosphere in one complete package.
       </p>
       {/* Inset pull-quote */}
       <p className="italic font-light text-blue-950/90 leading-[1.2] pl-6 border-l-4 border-amber-500" style={{ fontSize: "clamp(1.4rem, 2.6vw, 2rem)" }}>
        "The Magic delivers exactly that, flexible Sydney Harbour cruise charters suitable for every event style, every celebration, every iconic Sydney date."
       </p>
      </div>

      {/* THIRD ROW, Happy Birthday pink/orange group (wide cinematic) */}
      <div className="lg:col-span-12 mt-4">
       <Lightbox src="https://customer-assets.emergentagent.com/job_magic-harbour-party/artifacts/p40rjev5_boat%20cruises%20sydney.png" alt="Happy birthday group photo on the Magic · gold Happy Birthday balloon banner · pink and coral dressed guests with champagne · grazing table centerpiece · Sydney Harbour backdrop">
        <div className="relative rounded-3xl overflow-hidden ring-1 ring-blue-900/10 shadow-[0_30px_70px_-25px_rgba(0,0,0,0.35)] aspect-[21/9] hover:shadow-[0_40px_90px_-25px_rgba(0,0,0,0.45)] transition-shadow">
         <img src="https://customer-assets.emergentagent.com/job_magic-harbour-party/artifacts/p40rjev5_boat%20cruises%20sydney.png" alt="Happy birthday group photo on the Magic · gold Happy Birthday balloon banner · pink and coral dressed guests with champagne · grazing table centerpiece · Sydney Harbour backdrop" loading="lazy" className="absolute inset-0 w-full h-full object-cover hover:scale-105 transition-transform duration-1000" />
         <div className="absolute inset-0 bg-gradient-to-r from-pink-950/65 via-pink-900/15 to-transparent" />

         {/* Overlay text */}
         <div className="absolute inset-0 flex items-center">
          <div className="w-full px-7 lg:px-14 max-w-2xl">
           <div className="text-pink-200 text-[10px] uppercase tracking-[0.32em] font-black mb-3">Birthday on the harbour</div>
           <h3 className="text-white font-black tracking-tight leading-[0.95] drop-shadow-[0_6px_24px_rgba(0,0,0,0.5)]" style={{ fontSize: "clamp(1.5rem, 3.2vw, 2.5rem)", letterSpacing: "-0.02em" }}>
            From sweet sixteens to milestone birthdays, <span className="italic font-light text-amber-200">the Magic styles every celebration.</span>
           </h3>
          </div>
         </div>
        </div>
       </Lightbox>
      </div>

     </div>
    </div>
   </section>


   {/* ════════ SECTION 02, THE MAGIC DIFFERENCE · alternating cinematic storytelling rows ════════ */}
   <section className="relative bg-[#0A1E3D] text-white overflow-hidden" data-testid="home-magic-difference">
    {/* Soft cinematic glows */}
    <div className="absolute inset-0 pointer-events-none opacity-40">
     <div className="absolute -top-32 right-1/4 w-[36rem] h-[36rem] rounded-full bg-blue-500/15 blur-3xl" />
     <div className="absolute -bottom-32 left-1/4 w-[36rem] h-[36rem] rounded-full bg-amber-400/8 blur-3xl" />
    </div>

    <div className="relative mx-auto max-w-7xl px-5 lg:px-10 py-20 lg:py-24">
     {/* ── SECTION INTRO · 12-col magazine grid, headline + live "run sheet" card (kills the dead space) ── */}
     <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-end mb-12 lg:mb-16">
      <div className="lg:col-span-7">
       <div className="text-amber-300 text-[11px] uppercase tracking-[0.32em] font-bold inline-flex items-center gap-3 mb-7">
        <span className="w-8 h-px bg-amber-300/60" /> Section 02 · The Magic difference
       </div>
       <h2 className="font-black tracking-tight leading-[0.92]" style={{ fontSize: "clamp(2.25rem, 5vw, 4.5rem)", letterSpacing: "-0.025em" }}>
        Not a cruise.<br /><span className="italic font-normal text-amber-300">A memory in the making.</span>
       </h2>
       <p className="text-white/72 text-lg leading-[1.7] mt-7 max-w-xl">
        Every night on Magic is its own story. Sunsets behind the Opera House. Champagne under the Bridge. Dance floors that run till the harbour goes quiet.
       </p>
       {/* Inline meta strip, quick proof points under the headline */}
       <div className="mt-9 flex flex-wrap items-center gap-x-7 gap-y-3 text-white/65">
        <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] font-bold">
         <span className="w-1.5 h-1.5 rounded-full bg-amber-300 animate-pulse" /> Sails 7 nights / week
        </div>
        <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] font-bold">
         <Star className="w-3 h-3 text-amber-300" fill="currentColor" strokeWidth={0} /> 4.9 / 5 · 1,200+ reviews
        </div>
        <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] font-bold">
         <MapPin className="w-3 h-3 text-amber-300" /> King Street Wharf
        </div>
       </div>
      </div>

      {/* RIGHT, Private charter "venue specs" card · what they actually book, fills the right column */}
      <div className="lg:col-span-5">
       <div className="relative rounded-[1.75rem] bg-gradient-to-br from-white/[0.08] via-white/[0.03] to-transparent ring-1 ring-white/[0.12] backdrop-blur-xl p-6 lg:p-7 overflow-hidden">
        <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-amber-400/20 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -left-24 w-64 h-64 rounded-full bg-fuchsia-500/15 blur-3xl pointer-events-none" />
        <div className="relative">
         <div className="flex items-center justify-between mb-5">
          <div className="text-amber-300 text-[10px] uppercase tracking-[0.32em] font-bold inline-flex items-center gap-3">
           <span className="w-6 h-px bg-amber-300/60" /> The venue
          </div>
          <span className="text-[9px] uppercase tracking-[0.28em] font-black bg-amber-300 text-[#0A1E3D] px-2 py-1 rounded-full">Private hire</span>
         </div>
         <h3 className="font-black text-white text-2xl lg:text-[1.75rem] tracking-tight leading-[1.05] mb-2">
          Your event. Your guest list.<br /><span className="italic font-normal text-amber-300">The whole boat.</span>
         </h3>
         <p className="text-white/60 text-sm leading-relaxed mb-5">Charter the entire Magic catamaran exclusively, no shared decks, no strangers, no compromise.</p>
         {/* Spec grid */}
         <div className="grid grid-cols-2 gap-3 mb-5">
          <div className="rounded-xl bg-white/5 ring-1 ring-white/10 px-4 py-3.5">
           <div className="text-amber-300 inline-flex items-center gap-1.5 text-[9px] uppercase tracking-[0.28em] font-black mb-1.5">
            <Users className="w-3 h-3" /> Capacity
           </div>
           <div className="font-black text-white text-xl tracking-tight leading-none">10 to 100</div>
           <div className="text-white/50 text-[11px] mt-1">seated or standing</div>
          </div>
          <div className="rounded-xl bg-white/5 ring-1 ring-white/10 px-4 py-3.5">
           <div className="text-amber-300 inline-flex items-center gap-1.5 text-[9px] uppercase tracking-[0.28em] font-black mb-1.5">
            <Calendar className="w-3 h-3" /> Hire blocks
           </div>
           <div className="font-black text-white text-xl tracking-tight leading-none">3 · 4 · 5 hr</div>
           <div className="text-white/50 text-[11px] mt-1">your start time</div>
          </div>
          <div className="rounded-xl bg-white/5 ring-1 ring-white/10 px-4 py-3.5">
           <div className="text-amber-300 inline-flex items-center gap-1.5 text-[9px] uppercase tracking-[0.28em] font-black mb-1.5">
            <Music className="w-3 h-3" /> Included
           </div>
           <div className="font-black text-white text-xl tracking-tight leading-none">DJ + Bar</div>
           <div className="text-white/50 text-[11px] mt-1">crew, sound, lights</div>
          </div>
          <div className="rounded-xl bg-white/5 ring-1 ring-white/10 px-4 py-3.5">
           <div className="text-amber-300 inline-flex items-center gap-1.5 text-[9px] uppercase tracking-[0.28em] font-black mb-1.5">
            <Anchor className="w-3 h-3" /> Depart
           </div>
           <div className="font-black text-white text-xl tracking-tight leading-none">King St Wharf</div>
           <div className="text-white/50 text-[11px] mt-1">Darling Harbour</div>
          </div>
         </div>
         <Link to="/contact" className="group inline-flex w-full items-center justify-center gap-2 bg-amber-300 hover:bg-amber-200 text-[#0A1E3D] px-5 py-3.5 rounded-full font-black text-[13px] tracking-wide transition-colors" data-testid="venue-card-cta">
          Check your date <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
         </Link>
        </div>
       </div>
      </div>
     </div>

     {/* ── ROW 1 · Golden Hour · denser 7/5 split + image caption + pull-quote card (zero dead space) ── */}
     <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-stretch mb-14 lg:mb-20 reveal-up">
      <div className="lg:col-span-7 relative">
       <Lightbox src="https://customer-assets.emergentagent.com/job_magic-harbour-party/artifacts/lxhmsk0b_sydney%20cruises%20boats.png" alt="Bride to be and hens squad celebrating with champagne in front of Sydney Opera House and Harbour Bridge">
        <div className="relative rounded-[1.75rem] overflow-hidden aspect-[5/4] lg:aspect-[6/5] group shadow-[0_40px_90px_-30px_rgba(0,0,0,0.7)] ring-1 ring-white/10">
         <img src="https://customer-assets.emergentagent.com/job_magic-harbour-party/artifacts/lxhmsk0b_sydney%20cruises%20boats.png" alt="Bride to be and hens squad celebrating with champagne in front of Sydney Opera House and Harbour Bridge" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1200ms]" loading="lazy" />
         <div className="absolute inset-0 bg-gradient-to-t from-[#0A1E3D]/85 via-[#0A1E3D]/15 to-transparent" />
         <div className="absolute top-5 left-5 inline-flex items-center gap-2.5 bg-white/95 text-[#0A1E3D] px-4 py-2 rounded-full shadow-lg backdrop-blur ring-1 ring-white/30">
          <Sun className="w-4 h-4 text-amber-500" />
          <div className="text-[10px] uppercase tracking-[0.28em] font-black">Chapter 01 · Sunset</div>
         </div>
         {/* Bottom caption overlay */}
         <div className="absolute bottom-5 left-5 right-5">
          <div className="text-[10px] uppercase tracking-[0.32em] font-bold text-amber-300">Hens squad · Bridal toast</div>
          <div className="text-white font-black text-2xl lg:text-3xl tracking-tight leading-none mt-1.5 drop-shadow-lg">Sydney Harbour, 5:42pm</div>
         </div>
        </div>
       </Lightbox>
      </div>
      <div className="lg:col-span-5 flex flex-col justify-between gap-6">
       <div>
        <div className="text-amber-300 text-[11px] uppercase tracking-[0.32em] font-bold mb-5 inline-flex items-center gap-3">
         <span className="w-8 h-px bg-amber-300/60" /> 01 · Golden hour
        </div>
        <h3 className="font-black tracking-tight leading-[0.95] mb-5" style={{ fontSize: "clamp(1.85rem, 3.4vw, 3rem)", letterSpacing: "-0.02em" }}>
         The first toast<br /><span className="italic font-normal text-amber-300">always lands different</span> on the water.
        </h3>
        <p className="text-white/72 text-base lg:text-lg leading-[1.75]">
         We board you at golden hour, glass in hand. The Opera House catches the last light. Champagne is poured. The boat eases off King Street Wharf. From that first toast on, the night belongs to you.
        </p>
       </div>
       {/* Pull-quote card, fills the bottom of the column with social proof */}
       <div className="relative rounded-2xl bg-gradient-to-br from-amber-300/[0.14] via-white/[0.04] to-transparent ring-1 ring-amber-300/25 p-5 lg:p-6 backdrop-blur">
        <div className="absolute -top-3 left-5 bg-amber-300 text-[#0A1E3D] text-[9px] uppercase tracking-[0.32em] font-black px-2.5 py-1 rounded-full shadow-lg">Guest · Olivia M.</div>
        <div className="text-amber-300 font-black text-4xl leading-none mb-1 -mt-1">"</div>
        <p className="text-white/90 text-[15px] lg:text-base leading-snug italic">
         We pulled away from the wharf, the Opera House lit up gold, and everyone just stopped talking. Best night of the year.
        </p>
        <div className="flex items-center gap-1 mt-3">
         {[...Array(5)].map((_, i) => (<Star key={i} className="w-3.5 h-3.5 text-amber-300" fill="currentColor" strokeWidth={0} />))}
         <span className="ml-2 text-white/55 text-[10px] uppercase tracking-[0.24em] font-bold">Hens cruise · Nov 2026</span>
        </div>
       </div>
      </div>
     </div>

     {/* Row 2, TEXT LEFT (5) · IMAGE RIGHT (7), After Dark */}
     <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center mb-16 lg:mb-24 reveal-up">
      <div className="lg:col-span-5 order-2 lg:order-1">
       <div className="text-fuchsia-300 text-[11px] uppercase tracking-[0.32em] font-bold mb-5 inline-flex items-center gap-3">
        <span className="w-8 h-px bg-fuchsia-300/60" /> 02 · After dark
       </div>
       <h3 className="font-black tracking-tight leading-[0.95] mb-6" style={{ fontSize: "clamp(1.85rem, 3.4vw, 3rem)", letterSpacing: "-0.02em" }}>
        When the lights go down,<br /><span className="italic font-normal text-fuchsia-300">the boat goes up.</span>
       </h3>
       <p className="text-white/72 text-base lg:text-lg leading-[1.75]">
        Top-deck dance floor. Full bar. DJ on the booth. Skyline lights dancing in the water around you. This is the moment guests message you about for weeks afterwards.
       </p>
       {/* Stats row, pill chip style */}
       <div className="mt-8 grid grid-cols-3 gap-3 max-w-md">
        <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 px-4 py-4">
         <div className="text-amber-300 font-black text-2xl lg:text-3xl leading-none">5hr</div>
         <div className="text-white/55 text-[9px] uppercase tracking-[0.24em] font-bold mt-2">Party run-time</div>
        </div>
        <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 px-4 py-4">
         <div className="text-amber-300 font-black text-2xl lg:text-3xl leading-none">100</div>
         <div className="text-white/55 text-[9px] uppercase tracking-[0.24em] font-bold mt-2">Guests on board</div>
        </div>
        <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 px-4 py-4">
         <div className="text-amber-300 font-black text-2xl lg:text-3xl leading-none">DJ</div>
         <div className="text-white/55 text-[9px] uppercase tracking-[0.24em] font-bold mt-2">Sound system</div>
        </div>
       </div>
      </div>
      <div className="lg:col-span-7 order-1 lg:order-2">
       <Lightbox src={MAGIC_PHOTOS.magicDancefloorPacked} alt="Top-deck dance floor packed at night on the Magic catamaran · Sydney Harbour party cruise">
        <div className="relative rounded-[1.75rem] overflow-hidden aspect-[4/3] group shadow-[0_40px_90px_-30px_rgba(0,0,0,0.7)] ring-1 ring-white/10">
         <img src={MAGIC_PHOTOS.magicDancefloorPacked} alt="Top-deck dance floor packed at night on the Magic catamaran · Sydney Harbour party cruise" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1200ms]" loading="lazy" />
         <div className="absolute inset-0 bg-gradient-to-tl from-fuchsia-900/40 via-transparent to-transparent" />
         <div className="absolute bottom-5 right-5 inline-flex items-center gap-2.5 bg-fuchsia-500 text-white px-4 py-2 rounded-full shadow-lg ring-1 ring-fuchsia-300/40">
          <Music className="w-4 h-4" />
          <div className="text-[10px] uppercase tracking-[0.28em] font-black">Chapter 02 · After dark</div>
         </div>
        </div>
       </Lightbox>
      </div>
     </div>

    {/* ── ROW 3 · The Finale · FULL-BLEED cinematic photo with soft fade edges (no boxy frame) ── */}
    <div className="relative w-screen left-1/2 right-1/2 -translate-x-1/2 overflow-hidden -mt-4 lg:-mt-8" style={{ marginBottom: "-2rem" }}>
     <div className="relative h-[80vh] min-h-[600px] lg:h-[88vh] lg:max-h-[820px]">
      <img src={MAGIC_PHOTOS.nyeFireworksHero} alt="Sydney NYE fireworks from the water" className="absolute inset-0 w-full h-full object-cover scale-105" loading="lazy" />

      {/* Multi-direction fade overlays, dissolve image into the dark section */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#0A1E3D]/95 via-[#0A1E3D]/55 to-transparent" />
      <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-[#0A1E3D] via-[#0A1E3D]/70 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-[#0A1E3D] via-[#0A1E3D]/70 to-transparent" />
      {/* Subtle vignette at the right edge so image feels feathered */}
      <div className="absolute inset-y-0 right-0 w-32 lg:w-64 bg-gradient-to-l from-[#0A1E3D]/60 to-transparent" />

      {/* Glowing accent orbs */}
      <div className="absolute top-[15%] right-[20%] w-72 h-72 rounded-full bg-amber-400/20 blur-3xl animate-pulse pointer-events-none" style={{ animationDuration: "5s" }} />
      <div className="absolute bottom-[15%] right-[35%] w-56 h-56 rounded-full bg-fuchsia-500/15 blur-3xl animate-pulse pointer-events-none" style={{ animationDuration: "7s", animationDelay: "1.5s" }} />

      {/* INNER CONTENT, constrained to max-w-7xl */}
      <div className="absolute inset-0 flex items-center">
       <div className="mx-auto max-w-7xl w-full px-5 lg:px-10 relative">

        {/* Top-right vintage ticket stamp */}
        <div className="hidden md:block absolute top-4 right-5 lg:right-10 z-10">
         <div className="relative rounded-2xl bg-amber-300 text-[#0A1E3D] px-5 py-4 shadow-[0_20px_50px_-15px_rgba(251,191,36,0.7)] -rotate-3 ring-2 ring-amber-200/60">
          <div className="absolute -top-2 -left-2 w-4 h-4 rounded-full bg-[#0A1E3D]" />
          <div className="absolute -top-2 -right-2 w-4 h-4 rounded-full bg-[#0A1E3D]" />
          <div className="text-[9px] uppercase tracking-[0.32em] font-black opacity-70">Boarding pass</div>
          <div className="font-black text-2xl tracking-tight leading-none mt-1">NYE 2026</div>
          <div className="text-[10px] uppercase tracking-[0.28em] font-black mt-2 pt-2 border-t border-[#0A1E3D]/30">31 Dec · 9pm, Midnight</div>
         </div>
        </div>

        {/* Bottom-right pulsing live badge */}
        <div className="hidden lg:flex absolute bottom-4 right-10 z-10 items-center gap-2 px-3.5 py-2 rounded-full bg-black/40 backdrop-blur ring-1 ring-white/20 text-white">
         <span className="relative flex items-center">
          <span className="w-2 h-2 rounded-full bg-fuchsia-400" />
          <span className="absolute inset-0 w-2 h-2 rounded-full bg-fuchsia-400 animate-ping opacity-70" />
         </span>
         <span className="text-[10px] uppercase tracking-[0.28em] font-black">2026 selling fast</span>
        </div>

        {/* Main copy block */}
        <div className="max-w-2xl">
         <div className="text-amber-300 text-[11px] uppercase tracking-[0.32em] font-bold inline-flex items-center gap-3 mb-5">
          <span className="w-8 h-px bg-amber-300/60" /> 03 · The finale
         </div>
         <h3 className="font-black tracking-tight leading-[0.9] text-white" style={{ fontSize: "clamp(2.25rem, 5.5vw, 5rem)", letterSpacing: "-0.025em" }}>
          And then,<br /><span className="italic font-normal text-amber-300 inline-block pr-2 pb-1 -mr-2">the sky catches fire.</span>
         </h3>
         <p className="text-white/85 text-lg lg:text-xl leading-[1.6] mt-6 max-w-xl font-light">
          Front row to Sydney's biggest moments. No crowds. No queues. Just the water, the boat, and a view nobody on land will ever see.
         </p>

         {/* Proof-point strip */}
         <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-3 text-white/80">
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] font-bold">
           <Sparkles className="w-3 h-3 text-amber-300" fill="currentColor" strokeWidth={0} /> 9pm midnight fireworks
          </div>
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] font-bold">
           <Anchor className="w-3 h-3 text-amber-300" /> Bridge front-row
          </div>
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] font-bold">
           <Wine className="w-3 h-3 text-amber-300" /> Drinks included
          </div>
         </div>

         {/* Dual CTA row */}
         <div className="mt-9 flex flex-wrap items-center gap-3">
          <Link to="/cruises/nye-cruises-sydney-harbour" className="group inline-flex items-center gap-2.5 bg-amber-300 hover:bg-amber-200 text-[#0A1E3D] px-7 py-4 rounded-full font-black text-sm tracking-wide transition-colors shadow-[0_15px_40px_-10px_rgba(251,191,36,0.6)]">
           Get NYE tickets <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
          </Link>
          <Link to="/cruises" className="group inline-flex items-center gap-2.5 text-white hover:text-amber-300 px-5 py-4 rounded-full font-black text-sm tracking-wide transition-colors border border-white/25 hover:border-amber-300/60 backdrop-blur">
           See every kind of night <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
          </Link>
         </div>
        </div>
       </div>
      </div>
     </div>
    </div>
    </div>
   </section>

   {/* ════════ WHY CHOOSE MAGIC CRUISES, cinematic dark split · massive stats + photo + bold numbered features ════════ */}
   <section className="relative overflow-hidden text-white" data-testid="home-why-choose" style={{ background: "radial-gradient(at 15% 15%, hsl(220,70%,15%) 0%, transparent 50%), radial-gradient(at 85% 85%, hsl(245,55%,12%) 0%, transparent 50%), linear-gradient(180deg, hsl(225,65%,7%) 0%, hsl(230,60%,9%) 100%)" }}>
    {/* Atmospheric orbs */}
    <div className="absolute inset-0 pointer-events-none" aria-hidden="true, ">
     <div className="absolute top-[10%] left-[5%] w-[36rem] h-[36rem] rounded-full bg-amber-400/8 blur-3xl" />
     <div className="absolute bottom-[5%] right-[10%] w-[32rem] h-[32rem] rounded-full bg-pink-500/8 blur-3xl" />
     <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[44rem] h-[44rem] rounded-full bg-blue-500/5 blur-3xl" />
    </div>

    <div className="relative mx-auto max-w-7xl px-5 lg:px-10 py-24 lg:py-32">

     {/* TOP, Eyebrow + Massive cinematic stats trio */}
     <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 mb-16 lg:mb-24">
      <div className="lg:col-span-5">
       <div className="text-amber-300 text-[11px] uppercase tracking-[0.32em] font-bold inline-flex items-center gap-3 mb-7">
        <span className="w-8 h-px bg-amber-300/60" />
        <Star className="w-3.5 h-3.5" fill="currentColor" strokeWidth={0} />
        Why Magic
       </div>
       <h2 className="font-black tracking-tight leading-[0.95]" style={{ fontSize: "clamp(2rem, 4vw, 3.5rem)", letterSpacing: "-0.025em" }}>
        Sydney's most-booked <span className="italic font-light bg-clip-text text-transparent inline-block" style={{ backgroundImage: "linear-gradient(90deg, hsl(45,100%,72%) 0%, hsl(320,90%,72%) 60%, hsl(280,90%,75%) 100%)", paddingRight: "0.12em" }}>private charter.</span>
       </h2>
       <p className="text-white/72 text-base lg:text-lg leading-[1.7] mt-7 max-w-md">
        Twenty years on the water. Five thousand cruises in the wake. And not a single cookie-cutter night among them.
       </p>
      </div>

      {/* Animated stat trio with photo accent */}
      <div className="lg:col-span-7 grid grid-cols-3 gap-3 lg:gap-5">
       {[
        { num: "20+", label: "Years on the harbour", colour: "from-amber-400 to-amber-200" },
        { num: "5K+", label: "Cruises run", colour: "from-pink-400 to-fuchsia-300" },
        { num: "4.9★", label: "Guest rating", colour: "from-cyan-400 to-blue-300" }].map((s, i) => (
        <div key={i} className="relative group">
         <div className="absolute inset-0 rounded-2xl bg-white/[0.02] ring-1 ring-white/10 group-hover:ring-white/25 group-hover:bg-white/[0.04] transition-all" />
         <div className="relative px-3 lg:px-5 py-7 lg:py-9 text-center">
          <div className={`font-black leading-none tracking-tight bg-clip-text text-transparent bg-gradient-to-br ${s.colour} whitespace-nowrap`} style={{ fontSize: "clamp(2rem, 4.4vw, 4rem)", letterSpacing: "-0.04em" }}>
           {s.num}
          </div>
          <div className="text-white/55 text-[9px] lg:text-[10px] uppercase tracking-[0.2em] font-black mt-4 leading-snug">{s.label}</div>
         </div>
        </div>
       ))}
      </div>
     </div>

     {/* MAIN BODY, 6 cinematic numbered features with photo on the right */}
     <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">

      {/* LEFT, Big numbered feature list */}
      <div className="lg:col-span-7 space-y-2">
       {[
        { num: "01", Icon: Anchor, title: "Private exclusive charter", body: "The whole boat is yours. No shared sailings. No strangers. No randoms photobombing your night.", accent: "text-amber-300", bar: "from-amber-400 to-amber-300" },
        { num: "02", Icon: MapPin, title: "The harbour as your stage", body: "Glide past the Opera House. Pass under the Bridge. Anchor where the sunset hits Luna Park.", accent: "text-pink-300", bar: "from-pink-400 to-rose-300" },
        { num: "03", Icon: Wine, title: "Onboard chef. Real bar.", body: "Canapé menus from $30 pp. Seated buffets to $95 pp. Beverage packages, BYO option, signature cocktails.", accent: "text-rose-300", bar: "from-rose-400 to-pink-300" },
        { num: "04", Icon: Music, title: "Sound, lights & DJ-ready", body: "Top-deck dance floor. Built-in sound system. Add a pro DJ + photographer and your night runs itself.", accent: "text-fuchsia-300", bar: "from-fuchsia-400 to-violet-300" },
        { num: "05", Icon: Sparkles, title: "Tailored to your event", body: "3hr quick fix. Full day takeover. Custom styling, decor, balloon installs, marquee numbers, done.", accent: "text-emerald-300", bar: "from-emerald-400 to-teal-300" },
        { num: "06", Icon: Calendar, title: "Anywhere the harbour reaches", body: "King St Wharf standard. Pickups at Circular Quay, Rose Bay, Manly + private wharves on request.", accent: "text-cyan-300", bar: "from-cyan-400 to-sky-300" }].map((f, i) => (
        <div key={i} className="group relative grid grid-cols-[auto_1fr] gap-5 lg:gap-7 py-5 lg:py-6 border-b border-white/10 last:border-b-0 hover:bg-white/[0.02] transition-colors px-3 -mx-3 rounded-xl" data-testid={`why-feature-${i}`}>
         {/* Number column with gradient bar */}
         <div className="flex flex-col items-center pt-1.5">
          <div className={`font-black leading-none tracking-tight ${f.accent}`} style={{ fontSize: "clamp(1.5rem, 2.4vw, 2.25rem)", letterSpacing: "-0.03em" }}>
           {f.num}
          </div>
          <div className={`mt-3 w-[2px] flex-1 bg-gradient-to-b ${f.bar} opacity-40 group-hover:opacity-80 transition-opacity`} />
         </div>
         {/* Content */}
         <div className="flex-1">
          <div className="flex items-center gap-3 mb-2.5">
           <f.Icon className={`w-5 h-5 ${f.accent}`} strokeWidth={2.2} />
           <h3 className="text-white font-black tracking-tight text-lg lg:text-2xl leading-tight">
            {f.title}
           </h3>
          </div>
          <p className="text-white/65 text-sm lg:text-base leading-[1.7] max-w-2xl">{f.body}</p>
         </div>
        </div>
       ))}
      </div>

      {/* RIGHT, Sticky hero photo collage with floating review badge */}
      <div className="lg:col-span-5 lg:sticky lg:top-28">
       <div className="relative">
        {/* Glow halo */}
        <div className="absolute -inset-6 rounded-[2.5rem] bg-gradient-to-br from-amber-500/20 via-pink-500/15 to-purple-500/20 blur-3xl opacity-60" />

        <Lightbox src="https://customer-assets.emergentagent.com/job_magic-harbour-party/artifacts/zti7va75_sydney-magic-cruises.jpg" alt="Corporate team group photo on the MV Sydney Magic with Sydney CBD skyline, Harbour Bridge and Opera House in the background">
         <div className="relative rounded-[2rem] overflow-hidden ring-1 ring-white/15 shadow-[0_50px_120px_-30px_rgba(0,0,0,0.7)] aspect-[4/5]">
          <img src="https://customer-assets.emergentagent.com/job_magic-harbour-party/artifacts/zti7va75_sydney-magic-cruises.jpg" alt="Corporate team group photo on the MV Sydney Magic with Sydney CBD skyline, Harbour Bridge and Opera House in the background" loading="lazy" className="absolute inset-0 w-full h-full object-cover hover:scale-105 transition-transform duration-[1500ms]" />
          <div className="absolute inset-0 bg-gradient-to-t from-[hsl(225,65%,7%)]/70 via-transparent to-transparent" />

          {/* Top-left ribbon */}
          <div className="absolute top-5 left-5 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-300 text-blue-950 text-[10px] uppercase tracking-[0.28em] font-black shadow-lg">
           <Star className="w-3 h-3" fill="currentColor" strokeWidth={0} /> Sydney's #1 Charter
          </div>

          {/* Bottom-overlaid floating review badge */}
          <div className="absolute bottom-5 left-5 right-5 backdrop-blur-md bg-white/10 ring-1 ring-white/20 rounded-2xl p-4 lg:p-5">
           <div className="flex items-center gap-2 mb-2">
            {[1,2,3,4,5].map(i => <Star key={i} className="w-3.5 h-3.5 text-amber-300" fill="currentColor" strokeWidth={0} />)}
            <span className="text-amber-300 text-xs font-black ml-1">4.9</span>
            <span className="text-white/55 text-[10px] uppercase tracking-[0.22em] font-bold ml-auto">Google · 500+ reviews</span>
           </div>
           <p className="italic font-light text-white text-sm lg:text-base leading-snug">
            "Absolutely magic. The whole night was flawless, from the crew to the food to the sunset views. We're already booking next year."
           </p>
           <div className="text-white/55 text-[10px] uppercase tracking-[0.28em] font-bold mt-2.5">Arjun K. · Corporate charter</div>
          </div>
         </div>
        </Lightbox>

        {/* Floating CTA below image */}
        <div className="mt-6 lg:mt-8">
         <a href="#enquire" className="inline-flex items-center gap-2.5 w-full justify-center px-6 py-5 rounded-2xl bg-amber-400 hover:bg-amber-300 text-blue-950 font-black text-[12px] tracking-[0.22em] uppercase shadow-2xl shadow-amber-500/25 transition-all hover:scale-[1.02]" data-testid="why-choose-cta">
          Get your quote <ArrowRight className="w-4 h-4" />
         </a>
        </div>
       </div>
      </div>
     </div>
    </div>
   </section>



   {/* ════════ MEET THE CREW · the people who make every night work ════════ */}
   <section className="relative overflow-hidden text-[#0A1E3D]" data-testid="home-crew" style={{ background: "linear-gradient(180deg, #F5F8FC 0%, #EAF1F8 100%)" }}>
    <div className="absolute inset-0 pointer-events-none" aria-hidden="true, ">
     <div className="absolute -top-32 left-1/4 w-[28rem] h-[28rem] rounded-full bg-amber-200/30 blur-3xl" />
     <div className="absolute -bottom-32 right-1/4 w-[24rem] h-[24rem] rounded-full bg-blue-200/30 blur-3xl" />
    </div>
    <div className="relative max-w-7xl mx-auto px-5 lg:px-10 py-24 lg:py-32">
     <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
      <div className="lg:col-span-7 relative reveal-up">
       <div className="relative rounded-[1.75rem] overflow-hidden aspect-[4/3] shadow-[0_40px_90px_-30px_rgba(10,30,61,0.45)] ring-1 ring-blue-100">
        <img src="https://customer-assets.emergentagent.com/job_magic-harbour-party/artifacts/275v8648_magic-cruises-on-sydney-harbour-cruises.jpg" alt="Magic Cruises crew behind the licensed onboard bar with welcome cocktails on Sydney Harbour" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
       </div>
       <div className="hidden md:flex absolute -bottom-6 -left-4 lg:left-12 items-center gap-3 bg-[#0A1E3D] text-white px-5 py-3 rounded-2xl shadow-[0_20px_50px_-15px_rgba(0,0,0,0.5)] rotate-[-2deg]">
        <Star className="w-5 h-5 text-amber-300" fill="currentColor" strokeWidth={0} />
        <div>
         <div className="text-[10px] uppercase tracking-[0.28em] font-black">500+ five-star reviews</div>
         <div className="text-xs text-white/70 mt-0.5">Sydney's friendliest crew</div>
        </div>
       </div>
       <div className="hidden lg:flex absolute -top-5 -right-4 items-center gap-3 bg-amber-300 text-[#0A1E3D] px-5 py-3 rounded-2xl shadow-[0_20px_50px_-15px_rgba(0,0,0,0.4)] rotate-[2deg]">
        <Wine className="w-5 h-5" />
        <div>
         <div className="text-[10px] uppercase tracking-[0.28em] font-black">Welcome cocktails</div>
         <div className="text-xs opacity-80 mt-0.5">Poured before you board</div>
        </div>
       </div>
      </div>

      <div className="lg:col-span-5 reveal-up">
       <div className="text-blue-700 text-[11px] uppercase tracking-[0.32em] font-bold mb-5 inline-flex items-center gap-3">
        <span className="w-8 h-px bg-blue-700" /> Our crew
       </div>
       <h2 className="text-[#0A1E3D] font-black tracking-tight leading-[0.95]" style={{ fontSize: "clamp(2rem, 4.5vw, 3.75rem)", letterSpacing: "-0.022em" }}>
        Behind every great night,<br /><span className="italic font-normal text-blue-600">a crew who actually cares.</span>
       </h2>
       <p className="text-[#0A1E3D]/72 text-lg leading-[1.7] mt-7 max-w-md font-light">
        The hardest part of running a great party on Sydney Harbour isn't the boat. It's the team. Our captains, bar staff, chefs and hosts have run thousands of harbour celebrations between them and they treat every cruise like it's their own birthday.
       </p>
       <p className="text-[#0A1E3D]/72 text-lg leading-[1.7] mt-5 max-w-md font-light">
        Welcome cocktails poured before you board. The bar stays sharp and quick all night. Every dietary handled without you asking twice. And the kind of warm Sydney hospitality that quietly turns first-time guests into people who book back.
       </p>

       <div className="mt-9 grid grid-cols-3 gap-4 max-w-md">
        <div>
         <div className="text-amber-500 font-black text-2xl lg:text-3xl leading-none">RSA</div>
         <div className="text-[#0A1E3D]/60 text-[10px] uppercase tracking-[0.24em] font-bold mt-2">Trained bar staff</div>
        </div>
        <div>
         <div className="text-amber-500 font-black text-2xl lg:text-3xl leading-none">20+ yrs</div>
         <div className="text-[#0A1E3D]/60 text-[10px] uppercase tracking-[0.24em] font-bold mt-2">On the harbour</div>
        </div>
        <div>
         <div className="text-amber-500 font-black text-2xl lg:text-3xl leading-none">5,000+</div>
         <div className="text-[#0A1E3D]/60 text-[10px] uppercase tracking-[0.24em] font-bold mt-2">Cruises hosted</div>
        </div>
       </div>
      </div>
     </div>
    </div>
   </section>


   {/* ════════ CREATE YOUR CRUISE · HALLOWEEN PRIVATE PARTY FEATURE, deep purple/orange ════════ */}
   <section className="relative overflow-hidden text-white" data-testid="home-halloween-feature" style={{ background: "linear-gradient(135deg, hsl(265,55%,11%) 0%, hsl(285,60%,10%) 45%, hsl(15,55%,15%) 100%)" }}>
    {/* Atmospheric glow orbs */}
    <div className="absolute inset-0 pointer-events-none">
     <div className="absolute -top-32 -left-32 w-[40rem] h-[40rem] rounded-full bg-orange-500/22 blur-3xl" />
     <div className="absolute -bottom-32 -right-32 w-[40rem] h-[40rem] rounded-full bg-violet-600/30 blur-3xl" />
     <div className="absolute top-1/2 left-1/3 w-[28rem] h-[28rem] rounded-full bg-fuchsia-500/12 blur-3xl" />
    </div>

    <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
     <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
      {/* ── LEFT: content ── */}
      <div className="lg:col-span-6 reveal-up">
       <div className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/15 mb-7">
        <Moon className="w-3.5 h-3.5 text-orange-300" />
        <span className="text-white text-[10.5px] uppercase tracking-[0.32em] font-black">Halloween · Sydney Harbour</span>
       </div>

       <h2 className="font-display font-black tracking-tight leading-[1.02] pb-2 drop-shadow-2xl" style={{ fontSize: "clamp(2.5rem, 6.5vw, 5.5rem)", letterSpacing: "-0.02em" }}>
        Create your cruise.<br />
        <span className="italic font-light bg-gradient-to-r from-orange-300 via-rose-300 to-fuchsia-300 bg-clip-text text-transparent">Your way.</span>
       </h2>

       <p className="text-white/90 text-lg lg:text-xl leading-[1.7] font-light mt-7 max-w-xl">
        Bats. Pumpkins. Pole dancers. Pumpkin-spice cocktails on a candle-lit deck. Take over the entire MV Sydney Magic with your closest 50 to 120 friends and we'll transform it into Sydney's spookiest floating venue. <span className="font-medium text-orange-200">You write the brief, we build the night.</span>
       </p>

       {/* What's included list */}
       <ul className="mt-8 space-y-3.5">
        {[
         "Full private boat charter · up to 120 guests in costume", "Halloween décor: spider webs, jack-o-lanterns, fog machine, blood-red uplighting", "Themed canapés, pumpkin punch bowls and signature spooky cocktails", "DJ, dance floor, pole, photo booth and surprise entertainment", "Costume contest, themed prizes and Halloween karaoke add-ons"].map((item, i) => (
         <li key={i} className="flex items-start gap-3.5 text-white/85 text-[14.5px] lg:text-[15px] leading-[1.55]">
          <span className="w-5 h-5 mt-0.5 shrink-0 rounded-full flex items-center justify-center" style={{ background: "linear-gradient(135deg, hsl(25,100%,55%), hsl(345,90%,55%))" }}>
           <Sparkles className="w-3 h-3 text-white" strokeWidth={2.5} />
          </span>
          {item}
         </li>
        ))}
       </ul>

       <div className="flex flex-wrap gap-3 mt-10">
        <a href="#enquire" className="group inline-flex items-center gap-2 px-7 py-4 rounded-full font-black text-[12.5px] tracking-[0.18em] uppercase text-white shadow-2xl hover:scale-105 transition-all" style={{ background: "linear-gradient(95deg, hsl(25,100%,55%) 0%, hsl(345,90%,55%) 50%, hsl(285,75%,55%) 100%)", boxShadow: "0 25px 60px -15px hsla(15,80%,45%,0.55)" }}>
         Design my Halloween party
         <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </a>
        <Link to="/cruises/halloween-cruises-sydney-harbour" className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-white/8 hover:bg-white/15 backdrop-blur-md text-white font-bold text-[12.5px] tracking-[0.18em] uppercase border border-white/30 hover:border-white/55 transition-all">
         See Halloween details
        </Link>
       </div>
      </div>

      {/* ── RIGHT: overlapping editorial photo collage ── */}
      <div className="lg:col-span-6 reveal-up">
       <div className="relative h-[36rem] lg:h-[40rem] max-w-[34rem] mx-auto lg:max-w-full">
        {/* Soft orange/purple glow behind photos */}
        <div className="absolute -inset-6 rounded-[2rem] bg-gradient-to-br from-orange-500/30 via-fuchsia-500/20 to-violet-600/30 blur-3xl" />

        {/* Photo 1, big vampire deck, top-left, larger */}
        <div className="absolute top-0 left-0 w-[68%] aspect-[4/5] rounded-2xl overflow-hidden ring-1 ring-orange-400/40 shadow-[0_30px_80px_-25px_rgba(0,0,0,0.85)] rotate-[-3deg] hover:rotate-0 transition-transform duration-700">
         <img src={MAGIC_PHOTOS.halloweenCostumeParty} alt="Halloween costume cruise on Sydney Harbour · vampires + capes + candle-lit deck on MV Sydney Magic" loading="lazy" className="w-full h-full object-cover" />
         <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
         <div className="absolute top-3 left-3 px-3 py-1 rounded-full bg-black/65 backdrop-blur-md text-orange-300 text-[9px] uppercase tracking-[0.28em] font-black">Costume Night</div>
        </div>

        {/* Photo 2, pumpkin punch + pole, bottom-right, smaller */}
        <div className="absolute bottom-0 right-0 w-[58%] aspect-[4/5] rounded-2xl overflow-hidden ring-1 ring-fuchsia-400/40 shadow-[0_30px_80px_-25px_rgba(0,0,0,0.85)] rotate-[5deg] hover:rotate-0 transition-transform duration-700 z-10">
         <img src={MAGIC_PHOTOS.halloweenPoleParty} alt="Halloween cruise · jack-o-lanterns + pumpkin punch cocktails + costumed guests + pole on MV Sydney Magic" loading="lazy" className="w-full h-full object-cover" />
         <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
         <div className="absolute bottom-3 right-3 px-3 py-1 rounded-full bg-orange-500/95 text-white text-[9px] uppercase tracking-[0.28em] font-black flex items-center gap-1.5">
          <span className="w-1 h-1 rounded-full bg-white animate-pulse" />
          Pumpkin Punch
         </div>
        </div>

        {/* Sticker, top-right */}
        <div className="absolute top-[2%] right-[2%] z-20 px-4 py-2.5 rounded-full bg-white text-blue-950 text-[10px] uppercase tracking-[0.28em] font-black shadow-2xl rotate-[8deg]">
         <Sparkles className="w-3 h-3 inline mr-1.5 text-orange-500" /> Your night, your rules
        </div>
       </div>
      </div>
     </div>
    </div>
   </section>

   {/* ════════ SEO EDITORIAL · PARTY · NETWORKING · HENS (dark interlude) ════════ */}
   <section className="relative overflow-hidden text-white bg-[hsl(220,55%,6%)]" data-testid="home-seo-party">
    <div className="absolute inset-0 pointer-events-none opacity-50" aria-hidden="true, ">
     <div className="absolute -top-40 right-1/4 w-[42rem] h-[42rem] rounded-full bg-fuchsia-500/15 blur-3xl" />
     <div className="absolute -bottom-32 left-1/4 w-[36rem] h-[36rem] rounded-full bg-amber-400/10 blur-3xl" />
    </div>
    <div className="relative mx-auto max-w-7xl px-5 lg:px-10 py-24 lg:py-32">
     <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
      <div className="lg:col-span-5">
       <div className="text-fuchsia-300 text-[11px] uppercase tracking-[0.32em] font-bold inline-flex items-center gap-3 mb-7">
        <span className="w-8 h-px bg-fuchsia-300/60" /> Party · Networking · Hens
       </div>
       <h2 className="font-black tracking-tight leading-[0.95] mb-8" style={{ fontSize: "clamp(2rem, 4.5vw, 3.75rem)", letterSpacing: "-0.02em" }}>
        Party boat hire, networking cruises, <span className="italic font-light text-fuchsia-300">private celebrations.</span>
       </h2>
       {/* Stacked photo collage, pinks/purples to match section */}
       <div className="grid grid-cols-2 gap-3 mt-8">
        <div className="rounded-2xl overflow-hidden ring-1 ring-fuchsia-400/20 shadow-[0_25px_50px_-20px_rgba(168,85,247,0.45)] aspect-[4/5]">
         <img src="https://customer-assets.emergentagent.com/job_magic-harbour-party/artifacts/7ccel59u_sydney%20boat%20cruises.png" alt="Sydney birthday party cruise · Happy Birthday neon sign · pink and gold balloons · group celebrating with cake on board the Magic" loading="lazy" className="w-full h-full object-cover" />
        </div>
        <div className="rounded-2xl overflow-hidden ring-1 ring-fuchsia-400/20 shadow-[0_25px_50px_-20px_rgba(236,72,153,0.45)] aspect-[4/5] mt-10">
         <img src={MAGIC_PHOTOS.hensBridal} alt="Hens party cruise group cheers on Sydney Harbour · bridal sash · champagne celebration on the bow" loading="lazy" className="w-full h-full object-cover" />
        </div>
       </div>
      </div>
      <div className="lg:col-span-7 lg:pt-10 space-y-8">
       <div>
        <div className="text-amber-300 text-[11px] uppercase tracking-[0.28em] font-bold mb-3">Party boat hire Sydney</div>
        <p className="text-white/80 text-[17px] leading-[1.9]">
         Party boat hire Sydney has become one of the most searched event experiences across Sydney Harbour because private cruise charters offer something completely different to hotels, pubs and function centres. Instead of sitting in a traditional venue, guests can move throughout the vessel, enjoy open harbour views, dance, socialise and experience Sydney Harbour from a completely unique perspective.
        </p>
       </div>
       <div>
        <div className="text-fuchsia-300 text-[11px] uppercase tracking-[0.28em] font-bold mb-3">Business networking cruises</div>
        <p className="text-white/80 text-[17px] leading-[1.9]">
         Business networking cruises Sydney have also become increasingly popular among entrepreneurs, business owners and professional organisations looking for unique networking venues. Hosting networking events onboard a Sydney Harbour cruise charter creates stronger engagement, better conversations and more memorable business experiences compared to standard indoor venues. Women in business events, startup networking cruises and executive harbour functions all work exceptionally well onboard private cruise charters.
        </p>
       </div>
       <div>
        <div className="text-pink-300 text-[11px] uppercase tracking-[0.28em] font-bold mb-3">Hens party cruises</div>
        <p className="text-white/80 text-[17px] leading-[1.9]">
         Hens party cruises Sydney continue to perform strongly because private harbour cruise charters create a more exciting and social environment for celebration groups. Guests can enjoy music, dancing, drinks and incredible harbour scenery while celebrating onboard their own private charter vessel. Sydney Harbour party cruises create a completely different atmosphere compared to standard nightlife venues and restaurants.
        </p>
       </div>
      </div>
     </div>
    </div>
   </section>


   {/* ════════ SEO EDITORIAL · CLOSING LONG-READ ════════ */}
   <section className="relative overflow-hidden text-blue-950" data-testid="home-seo-closing" style={{ background: "linear-gradient(180deg, #F5F8FC 0%, #EAF1F8 60%, #F5F8FC 100%)" }}>
    <div className="absolute inset-0 pointer-events-none opacity-50" aria-hidden="true, ">
     <div className="absolute -top-40 left-1/3 w-[40rem] h-[40rem] rounded-full bg-amber-200/40 blur-3xl" />
     <div className="absolute -bottom-32 right-1/4 w-[36rem] h-[36rem] rounded-full bg-blue-200/40 blur-3xl" />
    </div>
    <div className="relative mx-auto max-w-7xl px-5 lg:px-10 py-24 lg:py-32">
     <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
      <div className="lg:col-span-5">
       <div className="text-pink-600 text-[11px] uppercase tracking-[0.32em] font-bold inline-flex items-center gap-3 mb-7">
        <span className="w-8 h-px bg-pink-500/60" /> Why Sydney Harbour stays unforgettable
       </div>
       <h2 className="font-black tracking-tight leading-[0.95]" style={{ fontSize: "clamp(2rem, 4.5vw, 3.75rem)", letterSpacing: "-0.02em" }}>
        Flexibility. Food. <span className="italic font-light text-pink-600">Atmosphere.</span> All in one harbour.
       </h2>
       <p className="text-blue-950/70 text-base lg:text-lg leading-[1.85] mt-7">
        One of the biggest advantages of private cruise hire Sydney is flexibility. Every Sydney Harbour charter can be tailored to suit different group sizes, event styles and entertainment preferences. Some guests prefer relaxed cocktail-style harbour cruises with grazing tables and lounge-style seating, while others choose full party cruise experiences with DJs, dance floors, lighting and entertainment throughout the vessel.
       </p>
      </div>
      <div className="lg:col-span-7 lg:pt-14 space-y-6">
       <p className="text-blue-950/75 text-[17px] leading-[1.9]">
        Food and beverage experiences are an important part of every Sydney Harbour cruise charter. The Magic offers flexible catering and drinks options suitable for birthday cruises, wedding cruises, corporate harbour charters and private party cruises. From cocktail food and canapés through to larger event catering packages, every harbour cruise event can be customised to suit the atmosphere and style of the occasion.
       </p>
       <p className="text-blue-950/75 text-[17px] leading-[1.9]">
        Sydney Harbour itself becomes one of the highlights of every private cruise charter. Guests experience uninterrupted harbour views while cruising past Circular Quay, Darling Harbour, Barangaroo, the Sydney Opera House and the Harbour Bridge. Sunset cruises across Sydney Harbour create incredible event moments as the skyline changes colour across the water, while evening harbour cruises transform into energetic social experiences as Sydney lights reflect across the harbour.
       </p>
       <p className="text-blue-950/75 text-[17px] leading-[1.9]">
        Private charter cruises Sydney are popular because they combine entertainment, scenery, music, catering and atmosphere all in one location. Instead of coordinating multiple venues or separate event spaces, guests enjoy one complete Sydney Harbour cruise experience from beginning to end. This convenience has made private boat hire Sydney one of the most sought-after event experiences for celebrations and business functions alike.
       </p>
      </div>
     </div>

     {/* Pull-quote divider with photo band */}
     <div className="my-16 lg:my-24">
      <div className="grid grid-cols-3 gap-3 lg:gap-5 mb-12 lg:mb-16">
       <Lightbox src="https://customer-assets.emergentagent.com/job_magic-harbour-party/artifacts/mcelm2qt_harbour-cruise-vivid-sydney.jpg" alt="Sydney Opera House sails lit in vibrant purple, blue and teal during Vivid Sydney · close-up from the harbour water">
        <div className="rounded-2xl overflow-hidden ring-1 ring-blue-950/10 shadow-lg aspect-[4/5] lg:aspect-[4/3] hover:shadow-xl transition-shadow">
         <img src="https://customer-assets.emergentagent.com/job_magic-harbour-party/artifacts/mcelm2qt_harbour-cruise-vivid-sydney.jpg" alt="Sydney Opera House sails lit in vibrant purple, blue and teal during Vivid Sydney · close-up from the harbour water" loading="lazy" className="w-full h-full object-cover hover:scale-105 transition-transform duration-1000" />
        </div>
       </Lightbox>
       <Lightbox src="https://customer-assets.emergentagent.com/job_magic-harbour-party/artifacts/v6ax31g3_VIVID-CRUISE-MAGIC-CRUISES.jpg" alt="Sydney Harbour Bridge lit in deep purple neon outlines during Vivid Sydney · Opera House in background · cruise view">
        <div className="rounded-2xl overflow-hidden ring-1 ring-blue-950/10 shadow-lg aspect-[4/5] lg:aspect-[4/3] lg:mt-10 hover:shadow-xl transition-shadow">
         <img src="https://customer-assets.emergentagent.com/job_magic-harbour-party/artifacts/v6ax31g3_VIVID-CRUISE-MAGIC-CRUISES.jpg" alt="Sydney Harbour Bridge lit in deep purple neon outlines during Vivid Sydney · Opera House in background · cruise view" loading="lazy" className="w-full h-full object-cover hover:scale-105 transition-transform duration-1000" />
        </div>
       </Lightbox>
       <Lightbox src={MAGIC_PHOTOS.championToast} alt="Champagne toast on Sydney Harbour cruise · friends celebrating with bubbly on board the Magic">
        <div className="rounded-2xl overflow-hidden ring-1 ring-blue-950/10 shadow-lg aspect-[4/5] lg:aspect-[4/3] hover:shadow-xl transition-shadow">
         <img src={MAGIC_PHOTOS.championToast} alt="Champagne toast on Sydney Harbour cruise · friends celebrating with bubbly on board the Magic" loading="lazy" className="w-full h-full object-cover hover:scale-105 transition-transform duration-1000" />
        </div>
       </Lightbox>
      </div>
      <div className="grid lg:grid-cols-12 gap-6 items-center">
       <div className="hidden lg:block lg:col-span-2"><span className="block h-px bg-blue-950/15" /></div>
       <p className="lg:col-span-8 italic font-light text-center text-blue-950/85 leading-[1.2]" style={{ fontSize: "clamp(1.5rem, 3vw, 2.5rem)" }}>
        "Sydney Harbour remains one of the most photographed and recognisable locations in Australia, making it the ideal setting for cruise charters, private boat hire and harbour celebrations."
       </p>
       <div className="hidden lg:block lg:col-span-2"><span className="block h-px bg-blue-950/15" /></div>
      </div>
     </div>

     {/* Closing copy band */}
     <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
      <div className="lg:col-span-7 space-y-6">
       <p className="text-blue-950/75 text-[17px] leading-[1.9]">
        Magic Cruises caters to a wide variety of Sydney Harbour cruise events including birthday cruises, wedding cruises, hens party cruises, engagement cruises, Christmas party cruises, corporate harbour cruises, EOFY events, networking functions, family celebrations and private harbour parties. Every Sydney Harbour cruise charter is designed to create an atmosphere that matches the style and energy of the event.
       </p>
       <p className="text-blue-950/75 text-[17px] leading-[1.9]">
        Christmas party cruises Sydney and EOFY harbour cruises are especially popular among businesses looking for unique staff celebrations and client entertainment experiences. Corporate boat hire Sydney gives businesses the opportunity to reward staff and entertain clients while showcasing the incredible scenery of Sydney Harbour. Private harbour cruise charters naturally create a more relaxed and enjoyable atmosphere for workplace celebrations.
       </p>
       <p className="text-blue-950/75 text-[17px] leading-[1.9]">
        Another reason Sydney Harbour cruise charters continue to grow is because guests are increasingly searching for experience-based events rather than traditional venues. People want events that feel exciting, interactive and memorable. Private harbour cruises combine the beauty of Sydney Harbour with entertainment, music, food and social experiences in a way few other venues can offer.
       </p>
       <p className="text-blue-950/75 text-[17px] leading-[1.9]">
        The Magic has become a popular choice for private harbour cruises because it provides flexible event spaces suitable for both casual and formal occasions. Whether you are planning a relaxed harbour cruise with family and friends or a large corporate cruise charter with entertainment and catering, every Sydney Harbour charter can be customised to suit your event goals.
       </p>
      </div>
      <div className="lg:col-span-5 lg:pl-10 lg:border-l border-blue-950/10">
       <div className="text-amber-600 text-[11px] uppercase tracking-[0.32em] font-bold mb-5">The closing thought</div>
       <h3 className="font-black tracking-tight leading-[1.05] mb-5" style={{ fontSize: "clamp(1.5rem, 2.4vw, 2.25rem)", letterSpacing: "-0.015em" }}>
        As demand keeps rising, <span className="italic font-light text-amber-600">the harbour keeps delivering.</span>
       </h3>
       <p className="text-blue-950/75 text-[17px] leading-[1.9] mb-6">
        As demand for private harbour cruises Sydney continues to rise, more people are turning to Sydney Harbour boat hire for birthdays, weddings, corporate events and private celebrations. Harbour cruise charters offer a unique combination of atmosphere, scenery and entertainment that creates unforgettable event experiences across Sydney Harbour.
       </p>
       <p className="text-blue-950/75 text-[17px] leading-[1.9] mb-6">
        Whether you are organising a birthday cruise Sydney, corporate cruise charter, wedding harbour cruise, networking event, engagement party or private celebration, Magic Cruises offers Sydney Harbour cruise experiences designed around your guests and your event vision.
       </p>
       <p className="text-blue-950/75 text-[17px] leading-[1.9]">
        From relaxed harbour cruise charters through to high-energy party boat hire Sydney experiences, every cruise onboard The Magic delivers incredible harbour views, memorable moments and one of the best private cruise charter experiences available on Sydney Harbour.
       </p>
      </div>
     </div>
    </div>
   </section>


   {/* ════════ FULL-BLEED IMAGE BREAK 2, EMOTIONAL CINEMATIC CTA ════════ */}
   <section className="relative min-h-[88vh] flex items-center overflow-hidden grain-noise" data-testid="home-emotional-cta">
    <div className="absolute inset-0">
     <img src={MAGIC_PHOTOS.magicDancefloorPacked} alt="Packed dance floor on the MV Sydney Magic at night · purple party lights · DJ · branded Magic floor mat" className="w-full h-full object-cover scale-110 animate-vivid-pan" />
    </div>
    {/* Soft golden/blue depth layers */}
    <div className="absolute inset-0 bg-[linear-gradient(105deg,hsla(220,60%,4%,0.78)_0%,hsla(220,60%,4%,0.15)_55%,hsla(28,90%,55%,0.18)_100%)]" />
    <div className="absolute inset-0 bg-gradient-to-t from-[hsl(220,55%,5%)] via-transparent to-[hsla(220,60%,4%,0.45)]" />
    <div className="cinema-skyline-glow" aria-hidden="true, " />
    <div className="cinema-fog fog-low" aria-hidden="true, " />
    <div className="cinema-reflection" aria-hidden="true, " style={{ animationDuration: "26s" }} />

    <div className="relative mx-auto max-w-6xl w-full px-5 lg:px-10 z-10">
     <div className="reveal-up">
      <div className="text-amber-300 text-[11px] uppercase tracking-[0.42em] font-bold mb-8 flex items-center gap-3">
       <span className="w-10 h-px bg-amber-300/70" /> A night on the water
      </div>
      <h2 className="text-white font-display font-black tracking-tight leading-[1.0] drop-shadow-[0_10px_36px_rgba(0,0,0,0.7)]" style={{ fontSize: "clamp(3rem, 9vw, 8rem)", letterSpacing: "-0.025em" }}>
       Your next<br />celebration<br /><span className="italic font-light ink-shimmer" style={{ fontSize: "0.72em", display: "inline-block", paddingBottom: "0.12em" }}>starts on Sydney Harbour.</span>
      </h2>
      <p className="text-white/92 text-xl md:text-2xl mt-10 max-w-xl leading-[1.55] font-light">No tour. No timetable. Just you, the people you love, and the most beautiful harbour in the world.</p>
      <div className="mt-12">
       <a href="#enquire" className="inline-flex items-center gap-4 pl-9 pr-7 py-6 rounded-full bg-amber-400 hover:bg-amber-300 text-blue-950 font-black text-base tracking-[0.18em] uppercase shadow-2xl shadow-amber-500/50 transition-all hover:scale-[1.03]">
        Plan your night
        <span className="w-9 h-9 rounded-full bg-blue-950 text-amber-300 flex items-center justify-center"><ArrowRight className="w-4 h-4" /></span>
       </a>
      </div>
     </div>
    </div>
   </section>

   {/* ════════ GALLERY, FULL-BLEED MASONRY ════════ */}
   <section className="relative py-24 overflow-hidden" data-testid="home-gallery">
    <div className="mx-auto max-w-7xl px-5 lg:px-10 mb-14">
     <Section n={3} label="Real nights · Real photos" Icon={Camera} />
     <h2 className="text-white font-black tracking-tight leading-[0.92] max-w-5xl" style={{ fontSize: "clamp(2.75rem, 7.5vw, 6.5rem)", letterSpacing: "-0.02em" }}>From the deck. <span className="italic font-light text-amber-300">In their words.</span></h2>
    </div>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5">
     {GALLERY.slice(0, 5).map((img, i) => (
      <div key={i} className={`relative overflow-hidden group ${i === 0 ? "md:col-span-2 md:row-span-2 aspect-square md:aspect-auto" : "aspect-square"}`}>
       <img src={img} alt={`Magic Cruises photo ${i + 1}`} loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000" />
       <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
     ))}
    </div>
    <div className="mx-auto max-w-7xl px-5 lg:px-10 mt-10">
     <Link to="/gallery" className="inline-flex items-center gap-2 text-white/85 hover:text-amber-300 text-xs font-black uppercase tracking-[0.28em] transition-colors">Full gallery <ArrowRight className="w-3.5 h-3.5" /></Link>
    </div>
   </section>

   {/* ════════ TESTIMONIALS, REFINED EDITORIAL CARDS · warm cream ════════ */}
   <section className="relative py-24 lg:py-32 bg-[hsl(35,45%,96%)] text-blue-950 overflow-hidden" data-testid="home-testimonials">
    <div className="absolute inset-0 pointer-events-none opacity-50">
     <div className="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 w-[60rem] h-[20rem] rounded-full bg-amber-300/25 blur-3xl" />
    </div>
    <div className="relative mx-auto max-w-7xl px-5 lg:px-10">
     <div className="flex items-end justify-between gap-6 flex-wrap mb-12">
      <div className="max-w-2xl">
       <div className="flex items-center gap-3 mb-4">
        <span className="w-8 h-px bg-pink-500/70" />
        <span className="text-pink-600 text-[10px] uppercase tracking-[0.42em] font-bold flex items-center gap-2"><Heart className="w-3 h-3" /> What guests say</span>
       </div>
       <h2 className="font-display font-black tracking-tight leading-[0.96]" style={{ fontSize: "clamp(2rem, 5vw, 3.75rem)", letterSpacing: "-0.02em" }}>
        4.9 <Star className="w-7 h-7 lg:w-9 lg:h-9 fill-amber-500 text-amber-500 inline -mt-2" /> <span className="text-blue-950/55 font-light italic">from</span> 2,500+ <span className="text-blue-950/55 font-light italic">Sydney guests.</span>
       </h2>
      </div>
      <div className="hidden md:flex items-center gap-4 text-blue-950/65 text-[10px] uppercase tracking-[0.32em] font-bold">
       <span>Google</span> <span className="w-1 h-1 rounded-full bg-blue-950/30" /> <span>Facebook</span> <span className="w-1 h-1 rounded-full bg-blue-950/30" /> <span>The Knot</span>
      </div>
     </div>

     {/* Card grid, 3 columns desktop, 2 tablet, 1 mobile · refined editorial style */}
     <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6" data-testid="tm-grid">
      {TESTIMONIALS.slice(0, 6).map((t, i) => {
       const initials = t.name.split(/[\s&]+/).filter(Boolean).slice(0, 2).map(s => s[0]).join("").toUpperCase();
       return (
        <article key={i} className="reveal-up relative bg-white/70 backdrop-blur-sm rounded-2xl p-6 lg:p-7 border border-blue-950/10 shadow-[0_2px_24px_-12px_rgba(15,23,42,0.18)] hover:shadow-[0_8px_40px_-16px_rgba(15,23,42,0.28)] hover:-translate-y-0.5 transition-all duration-500" data-testid={`tm-${i}`}>
         {/* Decorative quote mark */}
         <div className="absolute top-3 right-5 font-display text-[5rem] leading-none text-pink-500/15 select-none pointer-events-none" aria-hidden="true, ">"</div>

         {/* Stars */}
         <div className="flex gap-0.5 text-amber-500 mb-4 relative">
          {Array.from({ length: 5 }).map((_, k) => <Star key={k} className="w-3.5 h-3.5 fill-current" />)}
         </div>

         {/* Quote, refined sans, normal weight, smaller */}
         <p className="relative text-blue-950/85 text-[15px] lg:text-base leading-[1.65] font-normal">
          "{t.quote}"
         </p>

         {/* Footer */}
         <div className="mt-6 pt-5 border-t border-blue-950/10 flex items-center gap-3.5">
          <span className="shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 via-rose-400 to-pink-500 flex items-center justify-center text-white text-[11px] font-black tracking-wider shadow-sm">{initials}</span>
          <div className="min-w-0">
           <div className="text-blue-950 text-[13px] font-bold leading-tight truncate">{t.name}</div>
           <div className="text-blue-950/55 text-[10px] uppercase tracking-[0.18em] font-bold mt-1 truncate">{t.role} · {t.date}</div>
          </div>
         </div>
        </article>
       );
      })}
     </div>

     {/* Subtle "see all" link */}
     <div className="mt-10 flex items-center justify-center gap-3">
      <span className="w-12 h-px bg-blue-950/20" />
      <Link to="/gallery#reviews" className="text-blue-950/75 hover:text-pink-600 text-[10px] uppercase tracking-[0.32em] font-bold transition-colors flex items-center gap-2">Read all 2,500+ reviews <ArrowRight className="w-3 h-3" /></Link>
      <span className="w-12 h-px bg-blue-950/20" />
     </div>
    </div>
   </section>

   {/* ════════ AVAILABILITY ════════ */}
   <section id="availability" className="relative py-28 lg:py-32 overflow-hidden bg-white text-blue-950" data-testid="home-availability">
    <div className="mx-auto max-w-7xl px-5 lg:px-10">
     <div className="text-center max-w-3xl mx-auto mb-14">
      <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.32em] font-bold text-pink-600 uppercase mb-4"><span className="w-6 h-px bg-pink-500/60" /> Live availability <span className="w-6 h-px bg-pink-500/60" /></div>
      <h2 className="font-display font-black tracking-tight leading-[0.95]" style={{ fontSize: "clamp(2.5rem, 5.5vw, 4.5rem)" }}>Pick your date. <span className="italic font-light text-pink-600">We'll do the rest.</span></h2>
      <p className="text-slate-600 mt-5 text-base md:text-lg leading-[1.7]">Real-time availability for the MV Sydney Magic. Lock your night in.</p>
     </div>
     <div className="grid lg:grid-cols-2 gap-6 lg:gap-8 items-start">
      <AvailabilityCalendar hideCTA={true} selectedISO={availISO} onDateSelect={setAvailISO} />
      <BookingForm selectedDate={availISO} onClear={() => setAvailISO(null)} />
     </div>
    </div>
   </section>

   {/* ════════ FAQ ════════ */}
   <FAQSection />

   {/* ════════ CLOSING CTA, FULL-BLEED ════════ */}
   <section id="enquire" className="relative overflow-hidden py-28 lg:py-36 grain-noise" data-testid="home-enquiry">
    <div className="absolute inset-0">
     <img src={MAGIC_PHOTOS.vividMagicBoat} alt="MV Sydney Magic" className="w-full h-full object-cover scale-110" />
     <div className="absolute inset-0 bg-gradient-to-b from-[hsla(220,60%,4%,0.85)] via-[hsla(220,60%,4%,0.5)] to-[hsla(220,60%,4%,0.95)]" />
    </div>
    <div className="relative mx-auto max-w-7xl px-5 lg:px-10">
     <div className="grid lg:grid-cols-12 gap-12 items-start">
      <div className="lg:col-span-5">
       <Section n={4} label="Plan your night" Icon={Sparkles} />
       <h2 className="text-white font-black tracking-tight leading-[0.9]" style={{ fontSize: "clamp(3rem, 7.5vw, 6.5rem)", letterSpacing: "-0.02em" }}>Tell us your <span className="italic font-light text-amber-300">vision.</span></h2>
       <p className="text-white/92 mt-8 text-xl md:text-2xl leading-[1.65] font-light">Date, group size, vibe. We'll come back the same day with a tailored quote. Locked in.</p>
       <div className="mt-8 space-y-3">
        <a href={`tel:${CONTACT.phone1}`} className="flex items-center gap-3 text-white hover:text-amber-300 transition-colors group">
         <span className="w-12 h-12 rounded-full bg-amber-500/20 border border-amber-400/40 flex items-center justify-center group-hover:bg-amber-500/40 transition-colors"><Phone className="w-5 h-5 text-amber-300" /></span>
         <span className="text-2xl font-black">{CONTACT.phone1}</span>
        </a>
        <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-3 text-white/88 hover:text-white text-base transition-colors">
         <span className="w-12 h-12 rounded-full bg-white/10 border border-white/15 flex items-center justify-center"><Sparkles className="w-5 h-5 text-amber-300" /></span>{CONTACT.email}
        </a>
        <div className="flex items-center gap-3 text-white/75 text-base">
         <span className="w-12 h-12 rounded-full bg-white/5 border border-white/10 flex items-center justify-center"><MapPin className="w-5 h-5 text-amber-300" /></span>{CONTACT.embarkation}
        </div>
       </div>
      </div>
      <div className="lg:col-span-7 bg-white/[0.04] backdrop-blur-xl border border-white/15 rounded-3xl p-8 shadow-2xl">
       <EnquiryForm sourcePage="home" />
      </div>
     </div>
    </div>
   </section>
  </main>
 );
}
